import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toJpeg, toPng } from 'html-to-image';
import { FiEdit2, FiTrash2, FiChevronDown } from 'react-icons/fi';
import Modal from '@components/Modal';
import Container from '@components/Container';
import Button from '@components/Button';
import { supabase } from '@lib/supabase';
import { useAuth } from '@lib/useAuth';
import { useUserRole } from '@lib/useUserRole';
import { listVisionBoardBackgrounds } from '@lib/visionBoardBackgrounds';
import { logVisionBoardEvent } from '@lib/visionBoardEvents';
import {
  completeVisionBoardPageTimeEntry,
  generateVisionBoardPageTimeId,
  startVisionBoardPageTimeEntry,
  type VisionBoardPageTimeExitType,
} from '@lib/visionBoardPageTime';

// Lightweight id generator (no external deps)
const genId = () =>
  'g_' + Math.random().toString(36).slice(2, 9) + Date.now().toString(36);

interface BadgeRequirement {
  id?: string | null;
  name?: string | null;
  image_url?: string | null;
  color?: string | null;
  score?: number | null;
}

interface BadgeFeatureRequirementRow {
  badges: BadgeRequirement | null;
}

const buildBadgeAssetUrl = (value?: string | null) => {
  if (!value) return null;
  if (value.startsWith('http')) return value;
  const { data } = supabase.storage.from('badges').getPublicUrl(value);
  return data?.publicUrl || null;
};

type Goal = {
  id: string;
  imgSrc?: string; // data URL (optional)
  text: string; // HTML string
  bgColor?: string;
  font_size?: number;
  font_family?: string;
  text_color?: string;
  alignment?: 'left' | 'center' | 'right';
  container_shape?: 'square' | 'circle' | 'heart';
  has_shadow?: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  z: number;
};

const GOOGLE_FONTS = [
  { name: 'Inter', family: "'Inter', sans-serif" },
  { name: 'Roboto', family: "'Roboto', sans-serif" },
  { name: 'Montserrat', family: "'Montserrat', sans-serif" },
  { name: 'Playfair Display', family: "'Playfair Display', serif" },
  { name: 'Poppins', family: "'Poppins', sans-serif" },
] as const;

type AspectRatio = '16:9' | '9:16';
type CanvasBackground =
  | { type: 'preset'; value: string }
  | { type: 'custom'; value: string }
  | { type: 'none' };

type EditorSettings = {
  canvasBackground: CanvasBackground;
  backgroundDisplay: 'fill' | 'repeat';
  defaultFontSize: 'small' | 'medium' | 'large';
  defaultContainerShape: 'square' | 'circle' | 'heart';
  defaultHasShadow: boolean;
  defaultCardColor: string;
  canvasCorners: 'rounded' | 'square';
};

type BackgroundPreset = {
  id: string;
  label: string;
  value: string;
  defaultDisplay: EditorSettings['backgroundDisplay'];
};

interface ActiveVisionBoardPageTimeEntry {
  id: string;
  enteredAtIso: string;
  startedAtMs: number;
}

const MIN_TEXT_FONT_SIZE = 10;
const MAX_TEXT_FONT_SIZE = 80;
const DEFAULT_TEXT_FONT_SIZE = 18;
const DEFAULT_TEXT_COLOR = '#000000';
const SETTINGS_STORAGE_KEY = 'vision-board-editor-settings-v1';
const GOALS_STORAGE_KEY = 'vision-board-goals-v1';
const DEFAULT_CANVAS_BACKGROUND_COLOR = '#f7f4ef';
const DEFAULT_PRESET_BACKGROUND_DISPLAY: EditorSettings['backgroundDisplay'] =
  'repeat';
const DEFAULT_CUSTOM_BACKGROUND_DISPLAY: EditorSettings['backgroundDisplay'] =
  'fill';
const GOAL_PLACEMENT_MARGIN = 8;
const NEW_GOAL_OFFSET_SEQUENCE = [
  { dx: 56, dy: 40 },
  { dx: -56, dy: 40 },
  { dx: 56, dy: -40 },
  { dx: -56, dy: -40 },
  { dx: 72, dy: 0 },
  { dx: 0, dy: 72 },
] as const;
const MIN_PLACEMENT_DELTA = 4;
const FONT_SIZE_OPTIONS = [
  { id: 'small', label: 'Small', value: 14 },
  { id: 'medium', label: 'Medium', value: 18 },
  { id: 'large', label: 'Large', value: 24 },
] as const;
const FALLBACK_BACKGROUND_PRESETS: BackgroundPreset[] = [
  {
    id: 'corkboard',
    label: 'Corkboard',
    value: '/pictures/corkboard.jpg',
    defaultDisplay: 'repeat',
  },
  {
    id: 'sunrise',
    label: 'Sunrise',
    value: '/pictures/header-home.jpg',
    defaultDisplay: 'fill',
  },
  {
    id: 'mist',
    label: 'Morning Mist',
    value: '/pictures/header-home-2.jpg',
    defaultDisplay: 'fill',
  },
];
const DEFAULT_EDITOR_SETTINGS: EditorSettings = {
  canvasBackground: {
    type: 'preset',
    value: FALLBACK_BACKGROUND_PRESETS[0]!.value,
  },
  backgroundDisplay: FALLBACK_BACKGROUND_PRESETS[0]!.defaultDisplay,
  defaultFontSize: 'medium',
  defaultContainerShape: 'square',
  defaultHasShadow: true,
  defaultCardColor: '#ffffff',
  canvasCorners: 'square',
};

const VISION_BOARD_PAGE_NAME = 'vision-board';
const VISITOR_ID_STORAGE_KEY = 'xhalin_visitor_id';

const generateVisionBoardSessionId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.random() * 16;
    const value =
      char === 'x' ? Math.floor(rand) : (Math.floor(rand) & 0x3) | 0x8;
    return value.toString(16);
  });
};

const isValidUuid = (value: string | null): value is string => {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );
};

const getOrCreateVisitorId = (): string => {
  if (typeof window === 'undefined') {
    return generateVisionBoardSessionId();
  }

  try {
    const existing = window.localStorage.getItem(VISITOR_ID_STORAGE_KEY);
    if (isValidUuid(existing)) {
      return existing;
    }
    const next = generateVisionBoardSessionId();
    window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, next);
    return next;
  } catch {
    return generateVisionBoardSessionId();
  }
};

const sectionCardClass =
  'rounded-2xl border border-gray-100 bg-white/95 p-4 shadow-sm sm:p-5';

const resolveFontSize = (size: EditorSettings['defaultFontSize']) => {
  const match = FONT_SIZE_OPTIONS.find((option) => option.id === size);
  return match ? match.value : DEFAULT_TEXT_FONT_SIZE;
};

const getDisplayDimensions = (g: Goal) => {
  const isCircle = g.container_shape === 'circle';
  const isHeart = g.container_shape === 'heart';
  const isShaped = isCircle || isHeart;
  const shapedSize = isShaped ? Math.min(g.width, g.height) : 0;
  const width = isShaped ? shapedSize : g.width;
  const height = isShaped ? shapedSize : g.height;
  const leftOffset = isShaped ? (g.width - shapedSize) / 2 : 0;
  const topOffset = isShaped ? (g.height - shapedSize) / 2 : 0;
  return { width, height, leftOffset, topOffset };
};

const normalizeBackground = (input: unknown): CanvasBackground => {
  const obj = (input ?? {}) as { type?: string; value?: unknown };
  if (obj.type === 'none') {
    return { type: 'none' };
  }
  if (
    (obj.type === 'preset' || obj.type === 'custom') &&
    typeof obj.value === 'string' &&
    obj.value.trim()
  ) {
    return { type: obj.type as 'preset' | 'custom', value: obj.value };
  }
  return DEFAULT_EDITOR_SETTINGS.canvasBackground;
};

const defaultDisplayForBackground = (background: CanvasBackground) => {
  if (background.type === 'custom') {
    return DEFAULT_CUSTOM_BACKGROUND_DISPLAY;
  }
  if (background.type === 'preset') {
    const match = FALLBACK_BACKGROUND_PRESETS.find(
      (preset) => preset.value === background.value
    );
    return match?.defaultDisplay ?? DEFAULT_PRESET_BACKGROUND_DISPLAY;
  }
  return DEFAULT_PRESET_BACKGROUND_DISPLAY;
};

const readEditorSettings = (): EditorSettings => {
  if (typeof window === 'undefined') {
    return DEFAULT_EDITOR_SETTINGS;
  }

  try {
    const stored = sessionStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!stored) return DEFAULT_EDITOR_SETTINGS;
    const parsed = JSON.parse(stored) as Partial<EditorSettings>;
    const normalizedBackground = normalizeBackground(parsed.canvasBackground);
    const backgroundDisplay =
      parsed.backgroundDisplay === 'fill' ||
      parsed.backgroundDisplay === 'repeat'
        ? parsed.backgroundDisplay
        : defaultDisplayForBackground(normalizedBackground);
    const defaultFontSize = FONT_SIZE_OPTIONS.some(
      (option) => option.id === parsed.defaultFontSize
    )
      ? (parsed.defaultFontSize as EditorSettings['defaultFontSize'])
      : DEFAULT_EDITOR_SETTINGS.defaultFontSize;
    const defaultContainerShape =
      parsed.defaultContainerShape === 'square' ||
      parsed.defaultContainerShape === 'circle' ||
      parsed.defaultContainerShape === 'heart'
        ? parsed.defaultContainerShape
        : DEFAULT_EDITOR_SETTINGS.defaultContainerShape;
    const defaultHasShadow =
      typeof parsed.defaultHasShadow === 'boolean'
        ? parsed.defaultHasShadow
        : DEFAULT_EDITOR_SETTINGS.defaultHasShadow;
    const defaultCardColor =
      typeof parsed.defaultCardColor === 'string' && parsed.defaultCardColor
        ? parsed.defaultCardColor
        : DEFAULT_EDITOR_SETTINGS.defaultCardColor;
    const canvasCorners =
      parsed.canvasCorners === 'rounded' || parsed.canvasCorners === 'square'
        ? parsed.canvasCorners
        : DEFAULT_EDITOR_SETTINGS.canvasCorners;
    return {
      canvasBackground: normalizedBackground,
      backgroundDisplay,
      defaultFontSize,
      defaultContainerShape,
      defaultHasShadow,
      defaultCardColor,
      canvasCorners,
    };
  } catch {
    return DEFAULT_EDITOR_SETTINGS;
  }
};

const areEditorSettingsEqual = (a: EditorSettings, b: EditorSettings) => {
  if (a.canvasBackground.type !== b.canvasBackground.type) return false;
  const aVal =
    a.canvasBackground.type === 'none' ? null : a.canvasBackground.value;
  const bVal =
    b.canvasBackground.type === 'none' ? null : b.canvasBackground.value;
  if (aVal !== bVal) return false;
  return (
    a.backgroundDisplay === b.backgroundDisplay &&
    a.defaultFontSize === b.defaultFontSize &&
    a.defaultContainerShape === b.defaultContainerShape &&
    a.defaultHasShadow === b.defaultHasShadow &&
    a.defaultCardColor === b.defaultCardColor &&
    a.canvasCorners === b.canvasCorners
  );
};

const loadGoogleFont = (fontFamily: string) => {
  if (typeof document === 'undefined') return;
  const fontName = fontFamily.split(',')[0]?.replace(/['"]/g, '').trim();
  if (!fontName || fontName === 'Inter') return; // Inter is usually local or already loaded

  const id = `google-font-${fontName.toLowerCase().replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@400;600;700&display=swap`;
  document.head.appendChild(link);
};

export default function VisionBoard() {
  const { user, userFeatures } = useAuth();
  const { role } = useUserRole(user?.id ?? null);
  const isAdmin = role === 'admin';
  const hasVisionBoardFeature =
    userFeatures.includes('vision_board') || isAdmin;

  const navigate = useNavigate();
  const visitorIdRef = useRef<string>('');
  const visionBoardSessionIdRef = useRef<string>('');
  const pageViewLoggedRef = useRef(false);
  const boardLoadLoggedRef = useRef(false);
  const boardCreateLoggedRef = useRef(false);
  const goalsRef = useRef<Goal[]>([]);
  const autosaveTimerRef = useRef<number | null>(null);
  const autosaveReadyRef = useRef(false);
  const latestUserIdRef = useRef<string | null>(user?.id ?? null);
  const activePageTimeEntryRef = useRef<ActiveVisionBoardPageTimeEntry | null>(
    null
  );
  const pageTimeInitializedRef = useRef(false);
  const pageTimeSessionClosedRef = useRef(false);
  const pageTimeStartPromiseRef = useRef<Promise<void> | null>(null);
  const pendingTabHiddenTimeoutRef = useRef<number | null>(null);
  const nextGoalOffsetIndexRef = useRef(0);
  const [editorSettings, setEditorSettings] = useState<EditorSettings>(() =>
    readEditorSettings()
  );
  const editorSettingsRef = useRef<EditorSettings>(editorSettings);
  useEffect(() => {
    editorSettingsRef.current = editorSettings;
  }, [editorSettings]);
  const [hasStoredSettings] = useState(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(sessionStorage.getItem(SETTINGS_STORAGE_KEY));
  });
  const [backgroundPresets, setBackgroundPresets] = useState<
    BackgroundPreset[]
  >(FALLBACK_BACKGROUND_PRESETS);
  const [presetsLoaded, setPresetsLoaded] = useState(false);
  const [defaultPreset, setDefaultPreset] = useState<BackgroundPreset | null>(
    null
  );
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [showVisionBoardLockedModal, setShowVisionBoardLockedModal] =
    useState(false);
  const [visionBoardBadgeRequirements, setVisionBoardBadgeRequirements] =
    useState<BadgeRequirement[]>([]);
  const [previewBadge, setPreviewBadge] = useState<BadgeRequirement | null>(
    null
  );
  const [showSettingsSaved, setShowSettingsSaved] = useState(false);
  const settingsBaselineRef = useRef<EditorSettings | null>(null);
  const [goals, setGoals] = useState<Goal[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = sessionStorage.getItem(GOALS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    goalsRef.current = goals;
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem(GOALS_STORAGE_KEY, JSON.stringify(goals));
      } catch {
        // ignore
      }
    }
  }, [goals]);

  useEffect(() => {
    let active = true;
    async function loadVisionBoardRequirements() {
      try {
        const { data: featureRow, error: featureError } = await supabase
          .from('features')
          .select('id')
          .eq('key', 'vision_board')
          .maybeSingle();

        if (featureError) throw featureError;

        if (!featureRow?.id) {
          if (active) setVisionBoardBadgeRequirements([]);
          return;
        }

        const { data: badgeRows, error: badgeError } = await supabase
          .from('badge_features')
          .select(
            `
              badges (
                id,
                name,
                image_url,
                color,
                score
              )
            `
          )
          .eq('feature_id', featureRow.id);

        if (badgeError) throw badgeError;

        const rows =
          (
            badgeRows as unknown as
              | BadgeFeatureRequirementRow[]
              | null
              | undefined
          )
            ?.map((row) => row?.badges)
            .filter((badge): badge is BadgeRequirement => Boolean(badge)) ?? [];

        if (active) setVisionBoardBadgeRequirements(rows);
      } catch (err) {
        console.debug('Failed to load vision board requirements', err);
        if (active) setVisionBoardBadgeRequirements([]);
      }
    }

    loadVisionBoardRequirements();
    return () => {
      active = false;
    };
  }, []);

  const visionBoardBadgeNames = useMemo(
    () =>
      visionBoardBadgeRequirements
        .map((badge) => badge?.name)
        .filter((name): name is string => Boolean(name)),
    [visionBoardBadgeRequirements]
  );

  const visionBoardUnlockMessage = useMemo(() => {
    const baseMessage = 'Tap to learn how to unlock this feature.';

    if (!visionBoardBadgeNames || visionBoardBadgeNames.length === 0) {
      return baseMessage;
    }

    if (visionBoardBadgeNames.length === 1) {
      return `Earn the "${visionBoardBadgeNames[0]}" badge to unlock this feature. ${baseMessage}`;
    }

    if (visionBoardBadgeNames.length === 2) {
      return `Earn the "${visionBoardBadgeNames[0]}" or "${visionBoardBadgeNames[1]}" badge to unlock this feature. ${baseMessage}`;
    }

    const formatted = visionBoardBadgeNames.map((name) => `"${name}"`);
    const last = formatted.pop();
    return `Earn one of these badges to unlock this feature: ${formatted.join(', ')}, or ${last}. ${baseMessage}`;
  }, [visionBoardBadgeNames]);

  useEffect(() => {
    latestUserIdRef.current = user?.id ?? null;
  }, [user?.id]);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(() => {
    if (typeof window === 'undefined') return '16:9';
    return window.matchMedia('(max-width: 640px)').matches ? '9:16' : '16:9';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 640px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        setAspectRatio('9:16');
      }
    };
    mql.addEventListener('change', handler);
    // Initial check
    handler(mql);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const [zoom, setZoom] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showFloatingToolbar, setShowFloatingToolbar] = useState(false);
  const [pendingImage, setPendingImage] = useState<string | null>(null);
  const [pendingText, setPendingText] = useState('');
  const [pendingColor, setPendingColor] = useState(
    editorSettings.defaultCardColor
  );
  const [pendingColorIsTransparent, setPendingColorIsTransparent] =
    useState(false);
  const [pendingFontSize, setPendingFontSize] = useState<number>(
    resolveFontSize(editorSettings.defaultFontSize)
  );
  const [pendingFontFamily, setPendingFontFamily] = useState<string>(
    GOOGLE_FONTS[0].family
  );
  const [pendingTextColor, setPendingTextColor] = useState(DEFAULT_TEXT_COLOR);

  useEffect(() => {
    goals.forEach((g) => {
      if (g.font_family) loadGoogleFont(g.font_family);
    });
  }, [goals]);

  useEffect(() => {
    loadGoogleFont(pendingFontFamily);
  }, [pendingFontFamily]);
  const [textColorInputValue, setTextColorInputValue] =
    useState(DEFAULT_TEXT_COLOR);
  const [pendingAlignment, setPendingAlignment] = useState<
    'left' | 'center' | 'right'
  >('left');
  const [pendingVerticalAlignment, setPendingVerticalAlignment] = useState<
    'top' | 'center' | 'bottom'
  >('top');
  const [pendingContainerShape, setPendingContainerShape] = useState<
    'square' | 'circle' | 'heart'
  >(editorSettings.defaultContainerShape);
  const [pendingHasShadow, setPendingHasShadow] = useState<boolean>(
    editorSettings.defaultHasShadow
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [boardTitle, setBoardTitle] = useState('');
  const [boardTitleError, setBoardTitleError] = useState<string | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const backgroundInputRef = useRef<HTMLInputElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [siteHeaderHeight, setSiteHeaderHeight] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const headerEl = document.getElementById('site-header');
    const updateHeader = () =>
      setSiteHeaderHeight(headerEl?.getBoundingClientRect().height ?? 0);

    updateHeader();

    let headerRo: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && headerEl) {
      headerRo = new ResizeObserver(() => updateHeader());
      headerRo.observe(headerEl);
    } else {
      window.addEventListener('resize', updateHeader);
    }

    return () => {
      headerRo?.disconnect();
      window.removeEventListener('resize', updateHeader);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      const toolbarEl = toolbarRef.current;
      if (!toolbarEl) return;
      const { bottom } = toolbarEl.getBoundingClientRect();
      const stickyThreshold = (siteHeaderHeight || 0) + 24;
      setShowFloatingToolbar(bottom <= stickyThreshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [siteHeaderHeight]);

  const [boardBaseSize, setBoardBaseSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const exportRef = useRef<HTMLDivElement | null>(null);

  // Interaction state (drag or resize)
  const interactionRef = useRef<{
    id: string;
    mode: 'move' | 'resize';
    corner?: 'se' | 'sw' | 'ne' | 'nw';
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
    startWidth: number;
    startHeight: number;
    offsetX: number;
    offsetY: number;
    zoom: number;
  } | null>(null);

  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 1;
  const ZOOM_STEP = 0.05;
  const MAX_BOARD_TITLE_LENGTH = 80;
  const MAX_FILENAME_SLUG_LENGTH = 60;
  const PREVIEW_WIDTH = 400;
  const PREVIEW_QUALITY = 0.88;

  if (!visionBoardSessionIdRef.current) {
    visionBoardSessionIdRef.current = generateVisionBoardSessionId();
  }
  if (!visitorIdRef.current) {
    visitorIdRef.current = getOrCreateVisitorId();
  }

  const logVisionBoardInteraction = useCallback(
    (event: {
      eventType: string;
      eventCategory: string;
      eventLabel?: string | null;
      eventValue?: string | number | boolean | null | Record<string, unknown>;
    }) => {
      logVisionBoardEvent({
        ...event,
        visitorId: visitorIdRef.current,
        sessionId: visionBoardSessionIdRef.current,
        userId: user?.id ?? null,
        page: VISION_BOARD_PAGE_NAME,
      }).catch((error) => {
        if (import.meta.env.DEV) {
          console.error('Failed to log vision board event', error);
        }
      });
    },
    [user?.id]
  );

  const logOptionSelect = useCallback(
    (category: string, label?: string | null, value?: unknown) => {
      const normalizedValue =
        value === undefined
          ? undefined
          : typeof value === 'string' ||
              typeof value === 'number' ||
              typeof value === 'boolean' ||
              value === null
            ? value
            : (value as Record<string, unknown>);
      logVisionBoardInteraction({
        eventType: 'option_select',
        eventCategory: category,
        eventLabel: label ?? null,
        eventValue: normalizedValue,
      });
    },
    [logVisionBoardInteraction]
  );

  const logToggleEvent = useCallback(
    (label: string, value?: unknown) => {
      const normalizedValue =
        value === undefined
          ? undefined
          : typeof value === 'string' ||
              typeof value === 'number' ||
              typeof value === 'boolean' ||
              value === null
            ? value
            : (value as Record<string, unknown>);
      logVisionBoardInteraction({
        eventType: 'toggle',
        eventCategory: 'decoration',
        eventLabel: label,
        eventValue: normalizedValue,
      });
    },
    [logVisionBoardInteraction]
  );

  const resolveNavigationExitType =
    useCallback((): VisionBoardPageTimeExitType => {
      if (
        typeof performance !== 'undefined' &&
        typeof performance.getEntriesByType === 'function'
      ) {
        const entries = performance.getEntriesByType(
          'navigation'
        ) as PerformanceNavigationTiming[];
        const latestEntry = entries?.[entries.length - 1];
        if (latestEntry && latestEntry.type === 'reload') {
          return 'refresh';
        }
      }

      return 'unload';
    }, []);

  const startPageTimeTracking = useCallback(() => {
    if (
      pageTimeSessionClosedRef.current ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    if (activePageTimeEntryRef.current) {
      return;
    }

    const entryId = generateVisionBoardPageTimeId();
    const now = new Date();
    const entry: ActiveVisionBoardPageTimeEntry = {
      id: entryId,
      enteredAtIso: now.toISOString(),
      startedAtMs: now.getTime(),
    };

    activePageTimeEntryRef.current = entry;

    const promise = startVisionBoardPageTimeEntry({
      id: entryId,
      visitorId: visitorIdRef.current,
      sessionId: visionBoardSessionIdRef.current,
      userId: latestUserIdRef.current,
      page: VISION_BOARD_PAGE_NAME,
      enteredAt: entry.enteredAtIso,
    }).catch((error) => {
      if (import.meta.env.DEV) {
        console.error('[vision-board] failed to start page timing', error);
      }
    });

    pageTimeStartPromiseRef.current = promise;
    promise.finally(() => {
      if (pageTimeStartPromiseRef.current === promise) {
        pageTimeStartPromiseRef.current = null;
      }
    });
  }, []);

  const completePageTimeTracking = useCallback(
    (
      exitType: VisionBoardPageTimeExitType,
      options?: { allowRestart?: boolean }
    ) => {
      const entry = activePageTimeEntryRef.current;
      if (!entry) {
        if (!options?.allowRestart) {
          pageTimeSessionClosedRef.current = true;
        }
        return;
      }

      activePageTimeEntryRef.current = null;

      const finalize = async () => {
        if (pageTimeStartPromiseRef.current) {
          try {
            await pageTimeStartPromiseRef.current;
          } catch {
            // ignore start failures on completion
          }
        }

        const now = new Date();
        const durationMs = Math.max(0, now.getTime() - entry.startedAtMs);
        try {
          await completeVisionBoardPageTimeEntry({
            id: entry.id,
            visitorId: visitorIdRef.current,
            sessionId: visionBoardSessionIdRef.current,
            userId: latestUserIdRef.current,
            page: VISION_BOARD_PAGE_NAME,
            enteredAt: entry.enteredAtIso,
            exitedAt: now.toISOString(),
            durationSeconds: durationMs / 1000,
            exitType,
          });
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error(
              '[vision-board] failed to complete page timing',
              error
            );
          }
        }
      };

      void finalize();

      if (!options?.allowRestart) {
        pageTimeSessionClosedRef.current = true;
      }
    },
    []
  );

  function clampZoom(nextZoom: number) {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoom));
  }

  function clampFontSize(nextSize: number) {
    return Math.min(MAX_TEXT_FONT_SIZE, Math.max(MIN_TEXT_FONT_SIZE, nextSize));
  }

  // Keep the visual "base" size (zoom=1) in sync with the viewport.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setBoardBaseSize({ width, height });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [aspectRatio]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(
        SETTINGS_STORAGE_KEY,
        JSON.stringify(editorSettings)
      );
    } catch {
      // Ignore storage errors (private mode, quota, etc).
    }
  }, [editorSettings]);

  useEffect(() => {
    if (pageViewLoggedRef.current) return;
    pageViewLoggedRef.current = true;
    logVisionBoardInteraction({
      eventType: 'page_view',
      eventCategory: 'lifecycle',
    });

    // If we loaded goals from storage, mark board as created so we don't log it again
    if (goalsRef.current.length > 0) {
      boardCreateLoggedRef.current = true;
    }
  }, [logVisionBoardInteraction]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (pageTimeInitializedRef.current) {
      return;
    }

    pageTimeInitializedRef.current = true;
    startPageTimeTracking();

    const clearPendingTabHiddenTimeout = () => {
      if (pendingTabHiddenTimeoutRef.current !== null) {
        window.clearTimeout(pendingTabHiddenTimeoutRef.current);
        pendingTabHiddenTimeoutRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (pageTimeSessionClosedRef.current) return;
        clearPendingTabHiddenTimeout();
        pendingTabHiddenTimeoutRef.current = window.setTimeout(() => {
          pendingTabHiddenTimeoutRef.current = null;
          if (!pageTimeSessionClosedRef.current) {
            completePageTimeTracking('tab_hidden', { allowRestart: true });
          }
        }, 150);
      } else if (document.visibilityState === 'visible') {
        clearPendingTabHiddenTimeout();
        if (
          !pageTimeSessionClosedRef.current &&
          !activePageTimeEntryRef.current
        ) {
          startPageTimeTracking();
        }
      }
    };

    const handleBeforeUnload = () => {
      clearPendingTabHiddenTimeout();
      completePageTimeTracking(resolveNavigationExitType(), {
        allowRestart: false,
      });
    };

    const handlePageHide = () => {
      clearPendingTabHiddenTimeout();
      completePageTimeTracking(resolveNavigationExitType(), {
        allowRestart: false,
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      clearPendingTabHiddenTimeout();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      completePageTimeTracking('route_change', { allowRestart: false });
    };
  }, [
    completePageTimeTracking,
    resolveNavigationExitType,
    startPageTimeTracking,
  ]);

  useEffect(() => {
    if (boardLoadLoggedRef.current) return;
    if (!hasStoredSettings) return;
    boardLoadLoggedRef.current = true;
    logVisionBoardInteraction({
      eventType: 'board_load',
      eventCategory: 'board',
    });
  }, [hasStoredSettings, logVisionBoardInteraction]);

  useEffect(() => {
    if (!autosaveReadyRef.current) {
      autosaveReadyRef.current = true;
      return;
    }

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    if (goals.length === 0) return;
    autosaveTimerRef.current = window.setTimeout(() => {
      logVisionBoardInteraction({
        eventType: 'autosave',
        eventCategory: 'save',
      });
    }, 1500);

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [goals, logVisionBoardInteraction]);

  useEffect(() => {
    if (!showSettingsModal) return;
    settingsBaselineRef.current = editorSettingsRef.current;
    setShowSettingsSaved(false);
  }, [showSettingsModal]);

  useEffect(() => {
    if (!showSettingsModal) return;
    if (!settingsBaselineRef.current) return;
    if (!areEditorSettingsEqual(editorSettings, settingsBaselineRef.current)) {
      setShowSettingsSaved(true);
    }
  }, [editorSettings, showSettingsModal]);

  useEffect(() => {
    let isMounted = true;
    const loadPresets = async () => {
      try {
        const rows = await listVisionBoardBackgrounds();
        if (!isMounted) return;
        const activeRows = rows.filter((row) => row.active);
        const defaultRow = activeRows.find((row) => row.is_default);
        setDefaultPreset(
          defaultRow
            ? {
                id: defaultRow.id,
                label: defaultRow.title,
                value: defaultRow.image_url,
                defaultDisplay:
                  defaultRow.default_display ??
                  DEFAULT_PRESET_BACKGROUND_DISPLAY,
              }
            : null
        );
        setBackgroundPresets(
          activeRows.map((row) => ({
            id: row.id,
            label: row.title,
            value: row.image_url,
            defaultDisplay:
              row.default_display ?? DEFAULT_PRESET_BACKGROUND_DISPLAY,
          }))
        );
        setPresetsLoaded(true);
      } catch (err) {
        console.warn('Failed to load vision board backgrounds', err);
        setPresetsLoaded(true);
      }
    };
    loadPresets();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!presetsLoaded) return;

    if (backgroundPresets.length === 0) {
      if (editorSettingsRef.current.canvasBackground.type === 'preset') {
        setEditorSettings((prev) => ({
          ...prev,
          canvasBackground: { type: 'none' },
        }));
      }
      return;
    }
    const activeValue =
      editorSettingsRef.current.canvasBackground.type === 'preset'
        ? editorSettingsRef.current.canvasBackground.value
        : null;
    const fallbackPreset: BackgroundPreset =
      defaultPreset ?? backgroundPresets[0] ?? FALLBACK_BACKGROUND_PRESETS[0]!;
    if (
      activeValue &&
      !backgroundPresets.some((preset) => preset.value === activeValue)
    ) {
      setEditorSettings((prev) => ({
        ...prev,
        canvasBackground: {
          type: 'preset',
          value: fallbackPreset.value,
        },
        backgroundDisplay: fallbackPreset.defaultDisplay,
      }));
    }
  }, [backgroundPresets, defaultPreset, presetsLoaded]);

  useEffect(() => {
    if (hasStoredSettings) return;
    if (!defaultPreset) return;
    setEditorSettings((prev) => ({
      ...prev,
      canvasBackground: { type: 'preset', value: defaultPreset.value },
      backgroundDisplay: defaultPreset.defaultDisplay,
    }));
  }, [defaultPreset, hasStoredSettings]);

  // Add goal flow: click Add Goal -> open modal to set text/image
  const applySettingsDefaults = () => {
    setPendingImage(null);
    setPendingText('');
    setPendingColor(editorSettings.defaultCardColor);
    setPendingFontSize(resolveFontSize(editorSettings.defaultFontSize));
    setPendingFontFamily(GOOGLE_FONTS[0].family);
    setPendingTextColor(DEFAULT_TEXT_COLOR);
    setTextColorInputValue(DEFAULT_TEXT_COLOR);
    setPendingAlignment('left');
    setPendingVerticalAlignment('top');
    setPendingColorIsTransparent(false);
    setPendingContainerShape(editorSettings.defaultContainerShape);
    setPendingHasShadow(editorSettings.defaultHasShadow);
  };

  const clampWithinBoard = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

  const computeNextGoalPosition = (
    boardWidth: number,
    boardHeight: number,
    goalWidth: number,
    goalHeight: number
  ) => {
    // Small extra margin so card shadows and padding don't visually bleed off the edge
    const SAFE_EDGE_MARGIN = GOAL_PLACEMENT_MARGIN + 12;

    const fallbackX = Math.max(
      GOAL_PLACEMENT_MARGIN,
      boardWidth / 2 - goalWidth / 2
    );
    const fallbackY = Math.max(
      GOAL_PLACEMENT_MARGIN,
      boardHeight / 2 - goalHeight / 2
    );

    if (goalsRef.current.length === 0) {
      return {
        x: clampWithinBoard(
          fallbackX,
          GOAL_PLACEMENT_MARGIN,
          Math.max(
            GOAL_PLACEMENT_MARGIN,
            boardWidth - goalWidth - SAFE_EDGE_MARGIN
          )
        ),
        y: clampWithinBoard(
          fallbackY,
          GOAL_PLACEMENT_MARGIN,
          Math.max(
            GOAL_PLACEMENT_MARGIN,
            boardHeight - goalHeight - SAFE_EDGE_MARGIN
          )
        ),
      };
    }

    const minX = GOAL_PLACEMENT_MARGIN;
    const minY = GOAL_PLACEMENT_MARGIN;
    const maxX = Math.max(minX, boardWidth - goalWidth - SAFE_EDGE_MARGIN);
    const maxY = Math.max(minY, boardHeight - goalHeight - SAFE_EDGE_MARGIN);
    const lastGoal = goalsRef.current[goalsRef.current.length - 1];
    if (!lastGoal) {
      return {
        x: clampWithinBoard(fallbackX, minX, maxX),
        y: clampWithinBoard(fallbackY, minY, maxY),
      };
    }

    for (let i = 0; i < NEW_GOAL_OFFSET_SEQUENCE.length; i += 1) {
      const sequenceIndex =
        (nextGoalOffsetIndexRef.current + i) % NEW_GOAL_OFFSET_SEQUENCE.length;
      const { dx, dy } = NEW_GOAL_OFFSET_SEQUENCE[sequenceIndex]!;
      const candidateX = clampWithinBoard(lastGoal.x + dx, minX, maxX);
      const candidateY = clampWithinBoard(lastGoal.y + dy, minY, maxY);
      const distance =
        Math.abs(candidateX - lastGoal.x) + Math.abs(candidateY - lastGoal.y);
      if (distance > MIN_PLACEMENT_DELTA) {
        nextGoalOffsetIndexRef.current =
          (sequenceIndex + 1) % NEW_GOAL_OFFSET_SEQUENCE.length;
        return { x: candidateX, y: candidateY };
      }
    }

    nextGoalOffsetIndexRef.current =
      (nextGoalOffsetIndexRef.current + 1) % NEW_GOAL_OFFSET_SEQUENCE.length;
    const centeredX = clampWithinBoard(fallbackX, minX, maxX);
    const centeredY = clampWithinBoard(fallbackY, minY, maxY);
    if (
      Math.abs(centeredX - lastGoal.x) > MIN_PLACEMENT_DELTA ||
      Math.abs(centeredY - lastGoal.y) > MIN_PLACEMENT_DELTA
    ) {
      return { x: centeredX, y: centeredY };
    }

    const defaultOffset = 12;
    return {
      x: clampWithinBoard(lastGoal.x + defaultOffset, minX, maxX),
      y: clampWithinBoard(lastGoal.y - defaultOffset, minY, maxY),
    };
  };

  function handleAddGoalClick() {
    if (goals.length >= 8) return;
    applySettingsDefaults();
    setEditingId(null);
    setShowAddModal(true);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPendingImage(String(reader.result));
      logToggleEvent('goal_image', true);
    };
    reader.readAsDataURL(f);
    // reset value so same file can be uploaded again
    e.currentTarget.value = '';
  }

  function handleBackgroundFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      if (!result) return;
      setEditorSettings((prev) => ({
        ...prev,
        canvasBackground: { type: 'custom', value: result },
        backgroundDisplay: DEFAULT_CUSTOM_BACKGROUND_DISPLAY,
      }));
      logOptionSelect('background', 'custom_upload', f.name || null);
    };
    reader.readAsDataURL(f);
    e.currentTarget.value = '';
  }

  function handleCanvasDoubleClick(e: React.MouseEvent<HTMLDivElement>) {
    if ((e.target as Element).closest('[data-goal-card]')) return;
    handleAddGoalClick();
  }

  function savePending(asEdit?: boolean) {
    const plainText = stripHtml(pendingText).trim();
    if (!plainText && !pendingImage) return;
    const hasImage = Boolean(pendingImage);
    const isFirstGoal = goalsRef.current.length === 0;

    const boardWidth = boardBaseSize?.width ?? 800;
    const boardHeight = boardBaseSize?.height ?? 600;

    let defaultW = 440;
    let defaultH = 280;

    if (
      pendingContainerShape === 'circle' ||
      pendingContainerShape === 'heart'
    ) {
      const size = Math.min(defaultW, defaultH);
      defaultW = size;
      defaultH = size;
    }

    // Ensure the new item fits within the canvas, especially for 9:16 ratio
    const maxAllowedW = boardWidth - GOAL_PLACEMENT_MARGIN * 2;
    const maxAllowedH = boardHeight - GOAL_PLACEMENT_MARGIN * 2;

    if (defaultW > maxAllowedW || defaultH > maxAllowedH) {
      const ratio = defaultW / defaultH;
      if (maxAllowedW / ratio <= maxAllowedH) {
        defaultW = maxAllowedW;
        defaultH = maxAllowedW / ratio;
      } else {
        defaultH = maxAllowedH;
        defaultW = maxAllowedH * ratio;
      }
    }

    if (asEdit && editingId) {
      logVisionBoardInteraction({
        eventType: 'item_edit',
        eventCategory: 'goal',
        eventLabel: hasImage ? 'image' : 'text',
      });
      setGoals((s) =>
        s.map((g) => {
          if (g.id !== editingId) return g;
          let newW = g.width;
          let newH = g.height;
          if (
            (pendingContainerShape === 'circle' ||
              pendingContainerShape === 'heart') &&
            g.container_shape !== 'circle' &&
            g.container_shape !== 'heart'
          ) {
            const size = Math.min(g.width, g.height);
            newW = size;
            newH = size;
          }
          return {
            ...g,
            imgSrc: pendingImage || undefined,
            text: pendingText,
            bgColor: pendingColorIsTransparent ? 'transparent' : pendingColor,
            font_size: pendingFontSize,
            font_family: pendingFontFamily,
            text_color: pendingTextColor,
            alignment: pendingAlignment,
            vertical_alignment: pendingVerticalAlignment,
            container_shape: pendingContainerShape,
            has_shadow: pendingHasShadow,
            width: newW,
            height: newH,
          };
        })
      );
      setEditingId(null);
    } else {
      const id = genId();
      const { x, y } = computeNextGoalPosition(
        boardWidth,
        boardHeight,
        defaultW,
        defaultH
      );
      logVisionBoardInteraction({
        eventType: 'item_add',
        eventCategory: 'goal',
        eventLabel: hasImage ? 'image' : 'text',
      });
      if (isFirstGoal && !boardCreateLoggedRef.current) {
        boardCreateLoggedRef.current = true;
        logVisionBoardInteraction({
          eventType: 'board_create',
          eventCategory: 'board',
        });
      }
      setGoals((s) => [
        ...s,
        {
          id,
          imgSrc: pendingImage || undefined,
          text: pendingText,
          bgColor: pendingColorIsTransparent ? 'transparent' : pendingColor,
          font_size: pendingFontSize,
          font_family: pendingFontFamily,
          text_color: pendingTextColor,
          alignment: pendingAlignment,
          vertical_alignment: pendingVerticalAlignment,
          container_shape: pendingContainerShape,
          has_shadow: pendingHasShadow,
          x,
          y,
          width: defaultW,
          height: defaultH,
          z: Date.now(),
        },
      ]);
    }

    applySettingsDefaults();
    setShowAddModal(false);
  }

  function startEdit(id: string) {
    const g = goals.find((x) => x.id === id);
    if (!g) return;
    setPendingImage(g.imgSrc || null);
    setPendingText(g.text);
    const incomingBgColor = g.bgColor === 'transparent' ? '#ffffff' : g.bgColor;
    setPendingColor(incomingBgColor || '#ffffff');
    setPendingColorIsTransparent(g.bgColor === 'transparent');
    setPendingFontSize(g.font_size ?? DEFAULT_TEXT_FONT_SIZE);
    setPendingFontFamily(g.font_family || GOOGLE_FONTS[0].family);
    setPendingTextColor(g.text_color ?? DEFAULT_TEXT_COLOR);
    setTextColorInputValue(g.text_color ?? DEFAULT_TEXT_COLOR);
    setPendingAlignment(g.alignment ?? 'left');
    setPendingVerticalAlignment(
      (g as { vertical_alignment?: 'top' | 'center' | 'bottom' })
        .vertical_alignment ?? 'top'
    );
    setPendingContainerShape(g.container_shape ?? 'square');
    setPendingHasShadow(g.has_shadow ?? true);
    setEditingId(id);
    setShowAddModal(true);
  }

  function deleteGoal(id: string) {
    const isLastGoal =
      goalsRef.current.length === 1 && goalsRef.current[0]?.id === id;
    logVisionBoardInteraction({
      eventType: 'item_delete',
      eventCategory: 'goal',
    });
    if (isLastGoal) {
      logVisionBoardInteraction({
        eventType: 'board_delete',
        eventCategory: 'board',
      });
      boardCreateLoggedRef.current = false;
    }
    setGoals((s) => s.filter((g) => g.id !== id));
  }

  // Pointer events for drag/resize
  useEffect(() => {
    function onPointerMove(e: PointerEvent) {
      if (!interactionRef.current) return;
      const {
        id,
        mode,
        startX,
        startY,
        startLeft,
        startTop,
        startWidth,
        startHeight,
        offsetX,
        offsetY,
        zoom: interactionZoom,
        corner,
      } = interactionRef.current;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();

      const baseWidth = rect.width / interactionZoom;
      const baseHeight = rect.height / interactionZoom;

      if (mode === 'move') {
        const x = (e.clientX - rect.left) / interactionZoom - offsetX;
        const y = (e.clientY - rect.top) / interactionZoom - offsetY;
        const DRAG_MARGIN = 4;
        setGoals((s) =>
          s.map((g) => {
            if (g.id !== id) return g;
            const { width, height, leftOffset, topOffset } =
              getDisplayDimensions(g);
            const minX = -leftOffset + DRAG_MARGIN;
            const minY = -topOffset + DRAG_MARGIN;
            const maxX = baseWidth - width - leftOffset - DRAG_MARGIN;
            const maxY = baseHeight - height - topOffset - DRAG_MARGIN;

            return {
              ...g,
              x: Math.max(minX, Math.min(x, maxX)),
              y: Math.max(minY, Math.min(y, maxY)),
              z: Date.now(),
            };
          })
        );
      } else if (mode === 'resize') {
        const deltaX = (e.clientX - startX) / interactionZoom;
        const deltaY = (e.clientY - startY) / interactionZoom;
        const DRAG_MARGIN = 4;
        const minW = 100;
        const minH = 80;

        setGoals((s) =>
          s.map((g) => {
            if (g.id !== id) return g;

            const { leftOffset, topOffset } = getDisplayDimensions(g);
            const minX = -leftOffset + DRAG_MARGIN;
            const minY = -topOffset + DRAG_MARGIN;
            const maxX = baseWidth - leftOffset - DRAG_MARGIN;
            const maxY = baseHeight - topOffset - DRAG_MARGIN;

            const startRight = startLeft + startWidth;
            const startBottom = startTop + startHeight;

            let left = startLeft;
            let top = startTop;
            let right = startRight;
            let bottom = startBottom;

            switch (corner) {
              case 'sw':
                left = startLeft + deltaX;
                bottom = startBottom + deltaY;
                left = Math.min(Math.max(left, minX), startRight - minW);
                bottom = Math.min(Math.max(bottom, startTop + minH), maxY);
                break;
              case 'ne':
                right = startRight + deltaX;
                top = startTop + deltaY;
                right = Math.min(Math.max(right, startLeft + minW), maxX);
                top = Math.min(Math.max(top, minY), startBottom - minH);
                break;
              case 'nw':
                left = startLeft + deltaX;
                top = startTop + deltaY;
                left = Math.min(Math.max(left, minX), startRight - minW);
                top = Math.min(Math.max(top, minY), startBottom - minH);
                break;
              case 'se':
              default:
                right = startRight + deltaX;
                bottom = startBottom + deltaY;
                right = Math.min(Math.max(right, startLeft + minW), maxX);
                bottom = Math.min(Math.max(bottom, startTop + minH), maxY);
                break;
            }

            return {
              ...g,
              x: left,
              y: top,
              width: right - left,
              height: bottom - top,
              z: Date.now(),
            };
          })
        );
      }
    }

    function onPointerUp() {
      const interaction = interactionRef.current;
      if (interaction?.mode === 'move') {
        const goal = goalsRef.current.find((g) => g.id === interaction.id);
        if (goal) {
          const moved =
            Math.abs(goal.x - interaction.startLeft) > 1 ||
            Math.abs(goal.y - interaction.startTop) > 1;
          if (moved) {
            logVisionBoardInteraction({
              eventType: 'item_move',
              eventCategory: 'goal',
            });
          }
        }
      }
      interactionRef.current = null;
      document.body.style.userSelect = '';
      setDraggingId(null);
    }

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [logVisionBoardInteraction]);

  function onTilePointerDown(e: React.PointerEvent, g: Goal) {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const offsetX = (e.clientX - rect.left) / zoom - g.x;
    const offsetY = (e.clientY - rect.top) / zoom - g.y;
    setDraggingId(g.id);
    interactionRef.current = {
      id: g.id,
      mode: 'move',
      startX: e.clientX,
      startY: e.clientY,
      startLeft: g.x,
      startTop: g.y,
      startWidth: g.width,
      startHeight: g.height,
      offsetX,
      offsetY,
      zoom,
    };
    // bring to front
    setGoals((s) =>
      s.map((gg) => (gg.id === g.id ? { ...gg, z: Date.now() } : gg))
    );
    document.body.style.userSelect = 'none';
  }

  function onResizePointerDown(
    e: React.PointerEvent,
    g: Goal,
    corner: 'se' | 'sw' | 'ne' | 'nw'
  ) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    interactionRef.current = {
      id: g.id,
      mode: 'resize',
      corner,
      startX: e.clientX,
      startY: e.clientY,
      startLeft: g.x,
      startTop: g.y,
      startWidth: g.width,
      startHeight: g.height,
      offsetX: 0,
      offsetY: 0,
      zoom,
    };
    // bring to front
    setGoals((s) =>
      s.map((gg) => (gg.id === g.id ? { ...gg, z: Date.now() } : gg))
    );
    document.body.style.userSelect = 'none';
  }

  // Download: capture the actual DOM
  async function handleDownload() {
    const exportEl = exportRef.current;
    if (!exportEl) return;
    const active = document.activeElement as HTMLElement | null;

    try {
      logVisionBoardInteraction({
        eventType: 'export',
        eventCategory: 'download',
      });
      active?.blur();
      window.getSelection()?.removeAllRanges();
      setIsExporting(true);
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const url = await toPng(exportEl, {
        cacheBust: true,
        pixelRatio: 2,
        filter: (node) => {
          if (!(node instanceof HTMLElement)) return true;
          return !node.dataset.exportHide;
        },
      });

      // Clear download flag so the next visit to the download page triggers a fresh download
      sessionStorage.removeItem('xhalin_vision_board_downloaded');
      // Clear conversion flag so the next visit to the download page triggers a fresh conversion
      sessionStorage.removeItem('xhalin_vision_board_conversion_fired');

      navigate('/download-vision-board', { state: { imageData: url } });
    } finally {
      setIsExporting(false);
      active?.focus?.();
    }
  }

  async function handleSaveBoard(title: string) {
    const exportEl = exportRef.current;
    if (!exportEl || isSaving) return;
    const active = document.activeElement as HTMLElement | null;

    setSaveError(null);
    setSaveSuccess(false);

    const uploadedFiles: string[] = [];

    try {
      setIsSaving(true);
      active?.blur();
      window.getSelection()?.removeAllRanges();
      setIsExporting(true);
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Missing user');
      }

      const dataUrl = await toJpeg(exportEl, {
        cacheBust: true,
        pixelRatio: 2,
        quality: 0.9,
        filter: (node) => {
          if (!(node instanceof HTMLElement)) return true;
          return !node.dataset.exportHide;
        },
      });

      const blob = await fetch(dataUrl).then((res) => res.blob());
      const timestamp = Date.now();
      const filenameBase = `${slugifyTitle(title)}-${user.id}-${timestamp}`;
      const filename = `vision-board/${filenameBase}.jpg`;
      const previewFilename = `vision-board/previews/${filenameBase}-preview.jpg`;
      const previewBlob = await createPreviewBlob(dataUrl);

      const { error: uploadError } = await supabase.storage
        .from('vision_board')
        .upload(filename, blob, {
          contentType: 'image/jpeg',
        });

      if (uploadError) {
        throw uploadError;
      }
      uploadedFiles.push(filename);

      const { error: previewUploadError } = await supabase.storage
        .from('vision_board')
        .upload(previewFilename, previewBlob, {
          contentType: 'image/jpeg',
        });

      if (previewUploadError) {
        throw previewUploadError;
      }
      uploadedFiles.push(previewFilename);

      const { data: publicData } = supabase.storage
        .from('vision_board')
        .getPublicUrl(filename);
      const imageUrl = publicData?.publicUrl;

      const { data: previewPublicData } = supabase.storage
        .from('vision_board')
        .getPublicUrl(previewFilename);
      const previewImageUrl = previewPublicData?.publicUrl;

      if (!imageUrl) {
        throw new Error('Missing public URL');
      }

      if (!previewImageUrl) {
        throw new Error('Missing preview public URL');
      }

      const { error: insertError } = await supabase
        .from('vision_board_images')
        .insert({
          user_id: user.id,
          title,
          image_url: imageUrl,
          filename,
          preview_image_url: previewImageUrl,
          preview_filename: previewFilename,
        });

      if (insertError) {
        throw insertError;
      }

      setSaveSuccess(true);
      setShowSaveModal(false);
      setBoardTitle('');
    } catch (err) {
      if (uploadedFiles.length > 0) {
        const { error: cleanupError } = await supabase.storage
          .from('vision_board')
          .remove(uploadedFiles);
        if (cleanupError) {
          console.warn('[vision-board] cleanup failed', cleanupError);
        }
      }
      console.error('[vision-board] save failed', err);
      setSaveError("We couldn't save your vision board. Please try again.");
    } finally {
      setIsSaving(false);
      setIsExporting(false);
      active?.focus?.();
    }
  }

  function stripHtml(html: string) {
    if (typeof document === 'undefined') {
      return html.replace(/<[^>]*>/g, '');
    }
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  }

  function slugifyTitle(title: string) {
    const normalized = title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/-+$/g, '');
    const trimmed = normalized
      .slice(0, MAX_FILENAME_SLUG_LENGTH)
      .replace(/-+$/g, '');
    return trimmed || 'vision-board';
  }

  async function createPreviewBlob(dataUrl: string) {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Preview image failed to load'));
      image.src = dataUrl;
    });

    const width = PREVIEW_WIDTH;
    const scale = img.width ? width / img.width : 1;
    const height = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Preview canvas not available');
    }
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (!result) {
            reject(new Error('Preview export failed'));
            return;
          }
          resolve(result);
        },
        'image/jpeg',
        PREVIEW_QUALITY
      );
    });

    return blob;
  }

  function formatSelection(command: 'bold' | 'italic') {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false);
  }

  function formatAlignment() {
    if (!editorRef.current) return;
    editorRef.current.focus();

    let next: 'left' | 'center' | 'right' = 'left';
    if (pendingAlignment === 'left') next = 'center';
    else if (pendingAlignment === 'center') next = 'right';
    else next = 'left';

    setPendingAlignment(next);

    if (next === 'center') {
      document.execCommand('justifyCenter', false);
    } else if (next === 'right') {
      document.execCommand('justifyRight', false);
    } else {
      document.execCommand('justifyLeft', false);
    }
  }

  function formatVerticalAlignment() {
    if (!editorRef.current) return;
    editorRef.current.focus();

    let next: 'top' | 'center' | 'bottom' = 'top';
    if (pendingVerticalAlignment === 'top') next = 'center';
    else if (pendingVerticalAlignment === 'center') next = 'bottom';
    else next = 'top';

    setPendingVerticalAlignment(next);
    // No execCommand for vertical alignment; handled via layout styles
  }

  function insertBulletList() {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand('insertUnorderedList', false);
  }

  function applyFontSizeToSelection(size: number) {
    const editor = editorRef.current;
    if (typeof window === 'undefined' || !editor || !window.getSelection()) {
      return false;
    }
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return false;
    }
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      return false;
    }
    const span = document.createElement('span');
    span.style.fontSize = `${size}px`;
    span.appendChild(range.extractContents());
    range.insertNode(span);
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    selection.addRange(newRange);
    setPendingText(editor.innerHTML);
    return true;
  }

  function applyFontFamilyToSelection(family: string) {
    const editor = editorRef.current;
    if (typeof window === 'undefined' || !editor || !window.getSelection()) {
      return false;
    }
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return false;
    }
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      return false;
    }
    const span = document.createElement('span');
    span.style.fontFamily = family;
    span.appendChild(range.extractContents());
    range.insertNode(span);
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    selection.addRange(newRange);
    setPendingText(editor.innerHTML);
    return true;
  }

  function applyFontFamilyToEditor(family: string) {
    const editor = editorRef.current;
    if (!editor) return;
    editor.style.fontFamily = family;
  }

  function applyTextColorToSelection(color: string) {
    const editor = editorRef.current;
    if (typeof window === 'undefined' || !editor || !window.getSelection()) {
      return false;
    }
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return false;
    }
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      return false;
    }
    const span = document.createElement('span');
    span.style.color = color;
    span.appendChild(range.extractContents());
    range.insertNode(span);
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    selection.addRange(newRange);
    setPendingText(editor.innerHTML);
    return true;
  }

  function handleTextColorChange(color: string) {
    setTextColorInputValue(color);
    logOptionSelect('text_style', 'text_color', color);
    if (!applyTextColorToSelection(color)) {
      setPendingTextColor(color);
    }
  }

  async function handleShare() {
    const exportEl = exportRef.current;
    if (!exportEl) return;
    const active = document.activeElement as HTMLElement | null;

    try {
      logVisionBoardInteraction({
        eventType: 'export',
        eventCategory: 'share',
      });
      active?.blur();
      window.getSelection()?.removeAllRanges();
      setIsExporting(true);
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const url = await toPng(exportEl, {
        cacheBust: true,
        pixelRatio: 2,
        filter: (node) => {
          if (!(node instanceof HTMLElement)) return true;
          return !node.dataset.exportHide;
        },
      });

      const blob = await fetch(url).then((res) => res.blob());
      const file = new File([blob], 'vision-board.png', {
        type: 'image/png',
      });

      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function'
      ) {
        const shareData: ShareData = {
          title: 'Vision Board',
          files:
            typeof navigator.canShare === 'function' &&
            navigator.canShare({ files: [file] })
              ? [file]
              : undefined,
          url: window.location.href,
        };
        await navigator.share(shareData);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to share vision board', error);
      }
    } finally {
      setIsExporting(false);
      active?.focus?.();
    }
  }

  function applyFontSizeToEditor(size: number) {
    const editor = editorRef.current;
    if (!editor) return;
    editor.style.fontSize = `${size}px`;
  }

  function updateFontSize(delta: number) {
    editorRef.current?.focus();
    setPendingFontSize((prev) => {
      const next = clampFontSize(prev + delta);
      logOptionSelect('text_style', 'font_size', next);
      if (applyFontSizeToSelection(next)) {
        return next;
      }
      applyFontSizeToEditor(next);
      return next;
    });
  }

  function handleFontSizeInputChange(value: string) {
    if (value === '') {
      setPendingFontSize(0);
      return;
    }
    const next = parseInt(value, 10);
    if (isNaN(next)) return;

    // Allow typing values below MIN temporarily, but cap at MAX
    const capped = Math.min(MAX_TEXT_FONT_SIZE, next);
    setPendingFontSize(capped);

    if (capped >= MIN_TEXT_FONT_SIZE) {
      logOptionSelect('text_style', 'font_size', capped);
      if (!applyFontSizeToSelection(capped)) {
        applyFontSizeToEditor(capped);
      }
    }
  }

  function handleFontSizeBlur() {
    const clamped = clampFontSize(pendingFontSize);
    setPendingFontSize(clamped);
    if (!applyFontSizeToSelection(clamped)) {
      applyFontSizeToEditor(clamped);
    }
  }
  useEffect(() => {
    if (!showAddModal) return;
    const editor = editorRef.current;
    if (!editor) return;
    if (editor.innerHTML !== pendingText) {
      editor.innerHTML = pendingText || '';
    }
    const selection =
      typeof window !== 'undefined' ? window.getSelection() : null;
    if (!selection || selection.isCollapsed) {
      editor.style.fontSize = `${pendingFontSize}px`;
      editor.style.fontFamily = pendingFontFamily;
    }
  }, [pendingText, pendingFontSize, pendingFontFamily, showAddModal]);

  useEffect(() => {
    if (!showActionDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showActionDropdown &&
        !(e.target as Element).closest('.action-dropdown-container')
      ) {
        setShowActionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionDropdown]);

  const actionButtonBase =
    'item-selected inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base font-semibold shadow transition hover:brightness-95 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

  const openSaveModal = () => {
    if (goals.length === 0 || isSaving) return;

    if (!hasVisionBoardFeature) {
      setShowVisionBoardLockedModal(true);
      return;
    }

    setBoardTitle('');
    setBoardTitleError(null);
    setSaveError(null);
    setSaveSuccess(false);
    setShowSaveModal(true);
  };

  const confirmSaveBoard = async () => {
    const trimmedTitle = boardTitle.trim();
    if (!trimmedTitle) {
      setBoardTitleError('Please enter a name for your vision board.');
      return;
    }
    setBoardTitleError(null);
    logVisionBoardInteraction({
      eventType: 'save',
      eventCategory: 'save',
    });
    await handleSaveBoard(trimmedTitle);
  };

  const activeBackground =
    editorSettings.canvasBackground.type === 'none'
      ? null
      : editorSettings.canvasBackground.value;
  const backgroundMode = editorSettings.backgroundDisplay;
  const canShareBoard =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  // Add Item tooltip wrapper (appears on hover for desktop, tap or long-press on mobile).
  const AddItemWithTooltip: React.FC<{
    onClick: () => void;
    disabled?: boolean;
    className?: string;
    children?: React.ReactNode;
  }> = ({ onClick, disabled, className, children }) => {
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);
    const [posTop, setPosTop] = useState<number>(0);
    const [posLeft, setPosLeft] = useState<number>(0);
    const [placeBelow, setPlaceBelow] = useState(false);
    const longPressTimerRef = useRef<number | null>(null);
    const suppressClickRef = useRef(false);
    const srId = useRef('add-item-tooltip-' + genId()).current;

    const show = useCallback(() => {
      const btn = btnRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const vw = Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0
      );
      const tooltipWidth = Math.min(240, vw - 24);
      let left = rect.left + rect.width / 2;
      left = Math.max(
        12 + tooltipWidth / 2,
        Math.min(left, vw - 12 - tooltipWidth / 2)
      );
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const below = spaceBelow > 40 && spaceBelow > spaceAbove;
      // Use the rect edges as a base and rely on CSS transforms to fully offset the tooltip
      const topBase = below ? rect.bottom : rect.top;
      setPlaceBelow(below);
      setPosLeft(left);
      setPosTop(topBase);
      setVisible(true);
    }, []);

    const hide = useCallback(() => {
      setVisible(false);
      suppressClickRef.current = false;
    }, []);

    const onMouseEnter = () => {
      if (!(typeof window !== 'undefined' && 'ontouchstart' in window)) show();
    };
    const onMouseLeave = () => {
      if (!(typeof window !== 'undefined' && 'ontouchstart' in window)) hide();
    };

    const onTouchStart = () => {
      suppressClickRef.current = false;
      longPressTimerRef.current = window.setTimeout(() => {
        show();
        suppressClickRef.current = true;
        longPressTimerRef.current = null;
      }, 500) as unknown as number;
    };
    const onTouchEnd = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    const onClickInternal = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (typeof window !== 'undefined' && 'ontouchstart' in window) {
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
      onClick();
    };

    useEffect(() => {
      if (!visible) return;
      const onDocDown = (ev: Event) => {
        const t = ev.target as Node | null;
        if (!t) return;
        if (btnRef.current && btnRef.current.contains(t)) return;
        if (tooltipRef.current && tooltipRef.current.contains(t)) return;
        hide();
      };
      document.addEventListener('pointerdown', onDocDown, true);
      return () => document.removeEventListener('pointerdown', onDocDown, true);
    }, [visible, hide]);

    useEffect(() => {
      return () => {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
        }
      };
    }, []);

    return (
      <>
        <button
          ref={btnRef}
          type="button"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onClick={onClickInternal}
          aria-describedby={srId}
          disabled={disabled}
          className={className}
        >
          {children}
        </button>
        <span id={srId} className="sr-only">
          Add text, images, or titles to your board.
        </span>
        {visible && (
          <div
            ref={tooltipRef}
            role="status"
            aria-hidden={false}
            className="pointer-events-none fixed z-50 rounded-md bg-yellow-300 px-3 py-1 text-sm text-black shadow-sm transition-opacity duration-150"
            style={{
              left: posLeft,
              top: posTop,
              transform: placeBelow
                ? 'translateX(-50%) translateY(8px)'
                : 'translateX(-50%) translateY(calc(-100% - 8px))',
              maxWidth: 240,
            }}
          >
            Add text, images, or titles to your board.
          </div>
        )}
      </>
    );
  };

  const renderToolbarContent = (
    variant: 'default' | 'floating' = 'default'
  ) => (
    <div
      className={`relative z-20 mx-auto w-full max-w-none rounded-3xl border border-white/60 bg-white/90 px-4 py-4 shadow-lg backdrop-blur-lg sm:px-6 ${variant === 'floating' ? 'shadow-2xl ring-1 ring-black/5' : ''}`}
    >
      <div className="flex w-full flex-wrap items-center gap-3">
        <div className="flex flex-1 flex-wrap items-center justify-start gap-3">
          <div className="mr-2 hidden items-center rounded-full bg-gray-100 p-1 sm:flex">
            <button
              type="button"
              onClick={() => {
                setAspectRatio('16:9');
                logOptionSelect('layout', 'aspect_ratio', '16:9');
              }}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-all duration-200 ${aspectRatio === '16:9' ? 'item-selected shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              16:9
            </button>
            <button
              type="button"
              onClick={() => {
                setAspectRatio('9:16');
                logOptionSelect('layout', 'aspect_ratio', '9:16');
              }}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-all duration-200 ${aspectRatio === '9:16' ? 'item-selected shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              9:16
            </button>
          </div>
          <div className="hidden items-center gap-2 rounded-full bg-white/90 px-3 py-2 text-sm text-gray-700 shadow backdrop-blur sm:flex">
            <span className="mr-1 font-semibold">Zoom</span>
            <button
              type="button"
              onClick={() =>
                setZoom((z) => clampZoom(Number((z - ZOOM_STEP).toFixed(2))))
              }
              disabled={zoom <= MIN_ZOOM}
              className="rounded-full bg-white px-3 py-1 font-semibold shadow transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Zoom out"
            >
              -
            </button>
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              value={zoom}
              onChange={(e) => setZoom(clampZoom(Number(e.target.value)))}
              className="w-28"
              aria-label="Zoom slider"
            />
            <button
              type="button"
              onClick={() =>
                setZoom((z) => clampZoom(Number((z + ZOOM_STEP).toFixed(2))))
              }
              disabled={zoom >= MAX_ZOOM}
              className="rounded-full bg-white px-3 py-1 font-semibold shadow transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Zoom in"
            >
              +
            </button>
            <span className="hidden w-10 text-right text-sm tabular-nums sm:inline">
              {Math.round(zoom * 100)}%
            </span>
          </div>
          <AddItemWithTooltip
            onClick={handleAddGoalClick}
            disabled={goals.length >= 8}
            className={`${actionButtonBase} ${goals.length >= 8 ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            Add Item
          </AddItemWithTooltip>
          <button
            type="button"
            onClick={() => setShowSettingsModal(true)}
            className={actionButtonBase}
          >
            Settings
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* Desktop Actions */}
          <div className="hidden items-center gap-2 sm:flex">
            <button
              type="button"
              onClick={handleDownload}
              disabled={goals.length === 0 || isExporting}
              className={`${actionButtonBase} ${goals.length === 0 || isExporting ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              Download
            </button>
            {canShareBoard && (
              <button
                type="button"
                onClick={handleShare}
                disabled={goals.length === 0 || isExporting}
                className={`${actionButtonBase} ${goals.length === 0 || isExporting ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                Share
              </button>
            )}
            <button
              type="button"
              onClick={openSaveModal}
              disabled={isSaving || goals.length === 0}
              className={`${actionButtonBase} ${isSaving || goals.length === 0 ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              {isSaving ? 'Saving...' : 'Save as Breathing Background'}
            </button>
          </div>

          {/* Mobile Actions Dropdown */}
          {goals.length > 0 && (
            <div className="action-dropdown-container relative sm:hidden">
              <button
                type="button"
                onClick={() => setShowActionDropdown(!showActionDropdown)}
                className={`${actionButtonBase} flex items-center gap-1`}
              >
                Save
                <FiChevronDown
                  className={`transition-transform duration-200 ${showActionDropdown ? 'rotate-180' : ''}`}
                />
              </button>
              {showActionDropdown && (
                <div className="absolute right-0 top-full z-50 mt-2 w-56 origin-top-right rounded-2xl border border-gray-100 bg-white p-2 shadow-xl ring-1 ring-black/5 focus:outline-none">
                  <button
                    type="button"
                    onClick={() => {
                      setShowActionDropdown(false);
                      handleDownload();
                    }}
                    disabled={isExporting}
                    className="flex w-full items-center rounded-xl px-4 py-3 text-left text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                  >
                    Download
                  </button>
                  {canShareBoard && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowActionDropdown(false);
                        handleShare();
                      }}
                      disabled={isExporting}
                      className="flex w-full items-center rounded-xl px-4 py-3 text-left text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                    >
                      Share
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setShowActionDropdown(false);
                      openSaveModal();
                    }}
                    disabled={isSaving}
                    className="flex w-full items-center rounded-xl px-4 py-3 text-left text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save as Background'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {variant === 'default' && (saveError || saveSuccess) && (
        <div className="mt-3 text-center text-sm text-gray-600">
          {saveError ? saveError : 'Vision board saved.'}
        </div>
      )}
    </div>
  );

  return (
    <Container>
      <div className="py-8">
        <div className="mb-6 text-center">
          <div className="mx-auto max-w-5xl">
            <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
              Create Your Vision Board
            </h1>
            <p className="text-lg text-gray-600">
              Create a space that reflects where you're headed, then save it to
              use as a background.
            </p>
          </div>
        </div>

        <div ref={toolbarRef} className="mb-8">
          {renderToolbarContent('default')}
        </div>

        {showFloatingToolbar && (
          <div
            className="pointer-events-none fixed left-0 right-0 z-50 px-2 sm:px-6"
            style={{ top: siteHeaderHeight + 8 }}
          >
            <div className="pointer-events-auto">
              {renderToolbarContent('floating')}
            </div>
          </div>
        )}

        <div
          ref={viewportRef}
          style={{
            aspectRatio: aspectRatio === '16:9' ? '16/9' : '9/16',
          }}
          className={`relative z-10 mx-auto w-full overflow-auto border border-gray-200 shadow-inner transition-all duration-500 ease-in-out ${
            editorSettings.canvasCorners === 'rounded'
              ? 'rounded-2xl'
              : 'rounded-none'
          } ${aspectRatio === '16:9' ? 'max-h-[720px] max-w-full' : 'max-h-[85vh] max-w-[420px]'}`}
        >
          {/* Center the board when zoomed out */}
          <div className="flex min-h-full min-w-full items-center justify-center">
            <div
              ref={(node) => {
                canvasRef.current = node;
                exportRef.current = node;
              }}
              style={{
                width: boardBaseSize ? boardBaseSize.width * zoom : '100%',
                height: boardBaseSize ? boardBaseSize.height * zoom : '100%',
                backgroundColor: DEFAULT_CANVAS_BACKGROUND_COLOR,
                backgroundImage: activeBackground
                  ? `url(${activeBackground})`
                  : 'none',
                backgroundRepeat: activeBackground
                  ? backgroundMode === 'repeat'
                    ? 'repeat'
                    : 'no-repeat'
                  : 'no-repeat',
                backgroundSize: activeBackground
                  ? backgroundMode === 'repeat'
                    ? 'auto'
                    : 'cover'
                  : 'auto',
                backgroundPosition: activeBackground
                  ? backgroundMode === 'repeat'
                    ? 'left top'
                    : 'center'
                  : 'left top',
              }}
              onDoubleClick={handleCanvasDoubleClick}
              className={`relative overflow-hidden p-2 ${
                editorSettings.canvasCorners === 'rounded'
                  ? 'rounded-2xl'
                  : 'rounded-none'
              }`}
            >
              {/* SVG Clip Paths for Export */}
              <svg
                width="1"
                height="1"
                style={{
                  position: 'absolute',
                  opacity: 0,
                  pointerEvents: 'none',
                }}
                aria-hidden="true"
              >
                <defs>
                  <clipPath id="heart-clip" clipPathUnits="objectBoundingBox">
                    <path d="M 0.5, 0.945 L 0.431, 0.883 C 0.19, 0.662 0.03, 0.517 0.03, 0.34 C 0.03, 0.195 0.144, 0.081 0.289, 0.081 C 0.371, 0.081 0.45, 0.119 0.5, 0.179 C 0.55, 0.119 0.629, 0.081 0.711, 0.081 C 0.856, 0.081 0.97, 0.195 0.97, 0.34 C 0.97, 0.517 0.81, 0.662 0.569, 0.884 L 0.5, 0.945 Z" />
                  </clipPath>
                </defs>
              </svg>

              {isExporting && (
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    right: 12,
                    bottom: 12,
                    zIndex: 9999,
                    backgroundColor: 'black',
                    color: 'white',
                    padding: '6px 10px',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    transform: 'translateZ(0)',
                  }}
                >
                  Built with ❤️ using xhalin.om
                </div>
              )}

              {goals.length === 0 && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-gray-600">
                  <AddItemWithTooltip
                    onClick={handleAddGoalClick}
                    className={actionButtonBase}
                  >
                    Add Item
                  </AddItemWithTooltip>
                </div>
              )}

              {goals.map((g) => {
                const isDragging = draggingId === g.id;
                const shape = g.container_shape ?? 'square';
                const isCircle = shape === 'circle';
                const isHeart = shape === 'heart';
                const isShaped = isCircle || isHeart;
                const shapedSize = isShaped ? Math.min(g.width, g.height) : 0;
                const displayWidth = isShaped ? shapedSize : g.width;
                const displayHeight = isShaped ? shapedSize : g.height;
                const displayLeft = isShaped
                  ? g.x + (g.width - shapedSize) / 2
                  : g.x;
                const displayTop = isShaped
                  ? g.y + (g.height - shapedSize) / 2
                  : g.y;
                const isTransparentBackground = g.bgColor === 'transparent';
                const verticalAlign =
                  (g as { vertical_alignment?: 'top' | 'center' | 'bottom' })
                    .vertical_alignment ?? 'top';
                const shapedItemsClass =
                  verticalAlign === 'top'
                    ? 'items-start'
                    : verticalAlign === 'center'
                      ? 'items-center'
                      : 'items-end';
                const nonShapedJustifyClass =
                  verticalAlign === 'top'
                    ? 'justify-start'
                    : verticalAlign === 'center'
                      ? 'justify-center'
                      : 'justify-end';
                return (
                  <div
                    data-goal-card
                    key={g.id}
                    onPointerDown={(e) => onTilePointerDown(e, g)}
                    onDoubleClick={() => startEdit(g.id)}
                    style={{
                      left: displayLeft * zoom,
                      top: displayTop * zoom,
                      width: displayWidth * zoom,
                      height: displayHeight * zoom,
                      zIndex: g.z,
                      backgroundColor: isHeart
                        ? 'transparent'
                        : g.bgColor || '#ffffff',
                      boxShadow:
                        !isTransparentBackground && isDragging && !isHeart
                          ? '0 28px 60px -22px rgba(14,165,233,0.8)'
                          : undefined,
                      filter:
                        isHeart && !isTransparentBackground
                          ? isDragging
                            ? 'drop-shadow(0 15px 15px rgba(14,165,233,0.4))'
                            : g.has_shadow !== false
                              ? 'drop-shadow(0 5px 10px rgba(0,0,0,0.2))'
                              : undefined
                          : undefined,
                    }}
                    className={`group absolute ${isDragging ? 'scale-[1.02] cursor-grabbing' : 'cursor-grab'} ${isExporting ? 'pointer-events-none' : 'touch-none'} ${isCircle ? 'rounded-full' : isHeart ? '' : 'rounded-sm'} p-2 ${isTransparentBackground || isHeart || g.has_shadow === false ? '' : 'shadow-[0_10px_25px_-5px_rgba(0,0,0,0.3),0_8px_10px_-6px_rgba(0,0,0,0.3)]'} ${!isTransparentBackground && !isExporting ? 'transition-transform hover:scale-[1.01]' : ''}`}
                  >
                    <div
                      className={`relative flex h-full w-full overflow-hidden ${isCircle ? 'rounded-full' : ''}`}
                      style={{
                        backgroundColor: isHeart
                          ? g.bgColor || '#ffffff'
                          : undefined,
                        clipPath: isHeart ? 'url(#heart-clip)' : undefined,
                        WebkitClipPath: isHeart
                          ? 'url(#heart-clip)'
                          : undefined,
                      }}
                      role="region"
                      aria-label={stripHtml(g.text)}
                    >
                      {isShaped ? (
                        <>
                          {g.imgSrc && (
                            <img
                              src={g.imgSrc}
                              className="absolute inset-0 h-full w-full object-cover"
                              alt={stripHtml(g.text)}
                              draggable={false}
                              onDragStart={(e) => e.preventDefault()}
                            />
                          )}
                          <div
                            className={`relative flex h-full w-full ${shapedItemsClass} justify-center px-2`}
                          >
                            <div
                              style={{
                                fontSize: g.font_size ?? DEFAULT_TEXT_FONT_SIZE,
                                fontFamily:
                                  g.font_family || GOOGLE_FONTS[0].family,
                                color: g.text_color ?? DEFAULT_TEXT_COLOR,
                                textAlign: g.alignment || 'center',
                                marginTop: isHeart ? '-10%' : '0',
                              }}
                              className="max-h-full overflow-hidden whitespace-pre-wrap break-words font-medium [&_em]:italic [&_h1]:text-sm [&_h1]:font-semibold [&_li]:leading-tight [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5"
                              dangerouslySetInnerHTML={{ __html: g.text }}
                            />
                          </div>
                        </>
                      ) : (
                        <div
                          className={`flex h-full w-full flex-col ${nonShapedJustifyClass}`}
                        >
                          <div className="mb-2 px-1">
                            <div
                              style={{
                                fontSize: g.font_size ?? DEFAULT_TEXT_FONT_SIZE,
                                fontFamily:
                                  g.font_family || GOOGLE_FONTS[0].family,
                                color: g.text_color ?? DEFAULT_TEXT_COLOR,
                                textAlign: g.alignment || 'left',
                              }}
                              className="whitespace-pre-wrap break-words font-medium [&_em]:italic [&_h1]:text-sm [&_h1]:font-semibold [&_li]:leading-tight [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5"
                              dangerouslySetInnerHTML={{ __html: g.text }}
                            />
                          </div>

                          {g.imgSrc && (
                            <div className="relative flex-1 overflow-hidden rounded-sm">
                              <img
                                src={g.imgSrc}
                                className="h-full w-full object-cover"
                                alt={stripHtml(g.text)}
                                draggable={false}
                                onDragStart={(e) => e.preventDefault()}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div
                      data-export-hide
                      className="absolute right-2 top-2 z-10 flex gap-1 opacity-0 transition group-hover:opacity-100"
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => startEdit(g.id)}
                        className="rounded-full bg-white/90 p-1 shadow hover:bg-white"
                        aria-label="Edit goal"
                        title="Edit goal"
                      >
                        <FiEdit2 className="text-gray-700" size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(g.id)}
                        className="rounded-full bg-white/90 p-1 shadow hover:bg-white"
                        aria-label="Delete goal"
                        title="Delete goal"
                      >
                        <FiTrash2 className="text-red-600" size={16} />
                      </button>
                    </div>

                    {/* Resize handles */}
                    <div
                      data-export-hide
                      className="absolute bottom-1 right-1 hidden h-10 w-10 cursor-nwse-resize items-end justify-end opacity-0 transition group-hover:flex group-hover:opacity-100"
                      onPointerDown={(e) => onResizePointerDown(e, g, 'se')}
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-white/90 p-1 shadow">
                        <svg
                          viewBox="0 0 24 24"
                          width="12"
                          height="12"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          className="text-gray-400"
                        >
                          <path
                            d="M21 15L15 21M21 9L9 21"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                    <div
                      data-export-hide
                      className="absolute bottom-1 left-1 hidden h-10 w-10 cursor-nesw-resize items-end justify-start opacity-0 transition group-hover:flex group-hover:opacity-100"
                      onPointerDown={(e) => onResizePointerDown(e, g, 'sw')}
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-white/90 p-1 shadow">
                        <svg
                          viewBox="0 0 24 24"
                          width="12"
                          height="12"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          className="text-gray-400"
                        >
                          <path
                            d="M3 15L9 21M3 9L15 21"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />

        {showAddModal && (
          <Modal
            title={editingId ? 'Edit Goal' : 'Add Item'}
            className="max-h-[90vh] w-[95%] max-w-[95vw] !overflow-hidden sm:max-w-3xl"
            onClose={() => {
              setShowAddModal(false);
              setEditingId(null);
              setPendingImage(null);
            }}
          >
            <div
              className="flex flex-col"
              style={{ maxHeight: 'calc(90vh - 120px)' }}
            >
              <div className="flex-1 overflow-y-auto pr-2">
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Goal / Intention
                    </label>
                    <div className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <select
                          value={pendingFontFamily}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPendingFontFamily(val);
                            logOptionSelect('text_style', 'font_family', val);
                            if (!applyFontFamilyToSelection(val)) {
                              applyFontFamilyToEditor(val);
                            }
                          }}
                          className="rounded border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          {GOOGLE_FONTS.map((f) => (
                            <option
                              key={f.name}
                              value={f.family}
                              style={{ fontFamily: f.family }}
                            >
                              {f.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => formatSelection('bold')}
                          className="rounded border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Bold
                        </button>
                        <button
                          type="button"
                          onClick={() => formatSelection('italic')}
                          className="rounded border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Italic
                        </button>
                        <button
                          type="button"
                          onClick={formatAlignment}
                          aria-label={`${pendingAlignment.charAt(0).toUpperCase() + pendingAlignment.slice(1)} align`}
                          title={`${pendingAlignment.charAt(0).toUpperCase() + pendingAlignment.slice(1)} align`}
                          className="rounded border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            {pendingAlignment === 'left' && (
                              <path d="M4 6h16M4 12h10M4 18h14" />
                            )}
                            {pendingAlignment === 'center' && (
                              <path d="M6 6h12M4 12h16M7 18h10" />
                            )}
                            {pendingAlignment === 'right' && (
                              <path d="M4 6h16M10 12h10M6 18h14" />
                            )}
                          </svg>
                        </button>

                        <button
                          type="button"
                          onClick={formatVerticalAlignment}
                          aria-label={`${pendingVerticalAlignment.charAt(0).toUpperCase() + pendingVerticalAlignment.slice(1)} vertical align`}
                          title={`${pendingVerticalAlignment.charAt(0).toUpperCase() + pendingVerticalAlignment.slice(1)} vertical align`}
                          className="rounded border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            {pendingVerticalAlignment === 'top' && (
                              <g transform="rotate(90 12 12)">
                                <path d="M4 6h16M4 12h10M4 18h14" />
                              </g>
                            )}
                            {pendingVerticalAlignment === 'center' && (
                              <g transform="rotate(90 12 12)">
                                <path d="M6 6h12M4 12h16M7 18h10" />
                              </g>
                            )}
                            {pendingVerticalAlignment === 'bottom' && (
                              <g transform="rotate(90 12 12)">
                                <path d="M4 6h16M10 12h10M6 18h14" />
                              </g>
                            )}
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={insertBulletList}
                          aria-label="Bullet list"
                          title="Bullet list"
                          className="rounded border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M9 6h11M9 12h11M9 18h11" />
                            <circle cx="5" cy="6" r="1.5" fill="currentColor" />
                            <circle
                              cx="5"
                              cy="12"
                              r="1.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="5"
                              cy="18"
                              r="1.5"
                              fill="currentColor"
                            />
                          </svg>
                        </button>
                        <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1">
                          <span className="text-xs font-medium text-gray-600">
                            Text size
                          </span>
                          <div className="flex items-center gap-1 rounded-full bg-gray-100 px-1 py-0.5">
                            <button
                              type="button"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => updateFontSize(-1)}
                              disabled={pendingFontSize <= MIN_TEXT_FONT_SIZE}
                              aria-label="Decrease font size"
                              className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold text-gray-700 transition hover:bg-white disabled:cursor-not-allowed disabled:text-gray-400"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              value={pendingFontSize || ''}
                              onChange={(e) =>
                                handleFontSizeInputChange(e.target.value)
                              }
                              onBlur={handleFontSizeBlur}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.currentTarget.blur();
                                }
                              }}
                              min={MIN_TEXT_FONT_SIZE}
                              max={MAX_TEXT_FONT_SIZE}
                              className="w-10 bg-transparent text-center text-xs font-semibold text-gray-800 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            />
                            <button
                              type="button"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => updateFontSize(1)}
                              disabled={pendingFontSize >= MAX_TEXT_FONT_SIZE}
                              aria-label="Increase font size"
                              className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold text-gray-700 transition hover:bg-white disabled:cursor-not-allowed disabled:text-gray-400"
                            >
                              +
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-600">
                              Color
                            </span>
                            <input
                              type="color"
                              value={textColorInputValue}
                              onChange={(e) =>
                                handleTextColorChange(e.target.value)
                              }
                              aria-label="Select text color"
                              className="h-7 w-7 cursor-pointer rounded border border-gray-200 bg-white p-0.5"
                            />
                          </div>
                        </div>
                      </div>
                      <div
                        ref={editorRef}
                        contentEditable
                        role="textbox"
                        aria-multiline="true"
                        data-placeholder="e.g. Run a marathon, Learn to cook..."
                        onInput={(e) =>
                          setPendingText(
                            (e.currentTarget as HTMLDivElement).innerHTML
                          )
                        }
                        onPaste={(e) => {
                          e.preventDefault();
                          const text = e.clipboardData.getData('text/plain');
                          document.execCommand('insertText', false, text);
                        }}
                        style={{
                          color: pendingTextColor,
                          textAlign: pendingAlignment,
                          fontFamily: pendingFontFamily,
                          // Match textarea background to the selected card color
                          backgroundColor: pendingColorIsTransparent
                            ? 'transparent'
                            : pendingColor,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent:
                            pendingVerticalAlignment === 'top'
                              ? 'flex-start'
                              : pendingVerticalAlignment === 'center'
                                ? 'center'
                                : 'flex-end',
                        }}
                        className="min-h-[10rem] w-full resize-y overflow-y-auto p-3 outline-none [&_em]:italic [&_h1]:text-base [&_h1]:font-semibold [&_li]:my-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6"
                      />
                    </div>
                  </div>

                  <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
                    <div className="space-y-4">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Container
                        </label>
                        <div className="space-y-4 rounded-lg border border-gray-200 bg-white/50 p-4">
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-3">
                              <div className="text-sm font-medium text-gray-700">
                                Background
                              </div>
                              <div
                                role="group"
                                aria-label="Background"
                                className="ml-1 inline-flex shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-1"
                              >
                                {[
                                  { label: 'Visible', transparent: false },
                                  { label: 'Transparent', transparent: true },
                                ].map((option) => {
                                  const isActive =
                                    pendingColorIsTransparent ===
                                    option.transparent;
                                  return (
                                    <button
                                      aria-pressed={isActive}
                                      key={option.label}
                                      type="button"
                                      onClick={() => {
                                        setPendingColorIsTransparent(
                                          option.transparent
                                        );
                                        logToggleEvent(
                                          'goal_background_transparent',
                                          option.transparent
                                        );
                                      }}
                                      className={`rounded-md px-3 py-2 text-xs font-semibold transition ${isActive ? 'item-selected bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                      {option.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                          {!pendingColorIsTransparent && (
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="text-sm font-medium text-gray-700">
                                  Shape
                                </div>
                                <div
                                  role="group"
                                  aria-label="Shape"
                                  className="inline-flex shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-1"
                                >
                                  {(['square', 'circle', 'heart'] as const).map(
                                    (shape) => {
                                      const isActive =
                                        pendingContainerShape === shape;
                                      return (
                                        <button
                                          aria-pressed={isActive}
                                          key={shape}
                                          type="button"
                                          onClick={() =>
                                            setPendingContainerShape(shape)
                                          }
                                          className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold transition ${isActive ? 'item-selected bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                        >
                                          <span
                                            className={`h-4 w-4 border border-gray-300 ${shape === 'circle' ? 'rounded-full' : shape === 'heart' ? 'rotate-45' : 'rounded-sm'}`}
                                            style={
                                              shape === 'heart'
                                                ? {
                                                    clipPath:
                                                      'url(#heart-clip)',
                                                    WebkitClipPath:
                                                      'url(#heart-clip)',
                                                    transform:
                                                      'scale(1.2) rotate(0deg)',
                                                  }
                                                : {}
                                            }
                                            aria-hidden="true"
                                          />
                                          {shape.charAt(0).toUpperCase() +
                                            shape.slice(1)}
                                        </button>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          {!pendingColorIsTransparent && (
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-3">
                                <label className="shrink-0 text-sm font-medium text-gray-700">
                                  Shadow
                                </label>
                                <div className="inline-flex shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-1">
                                  {[
                                    { label: 'On', value: true },
                                    { label: 'Off', value: false },
                                  ].map((option) => {
                                    const isActive =
                                      pendingHasShadow === option.value;
                                    return (
                                      <button
                                        key={option.label}
                                        type="button"
                                        onClick={() => {
                                          setPendingHasShadow(option.value);
                                          logToggleEvent(
                                            'goal_shadow',
                                            option.value
                                          );
                                        }}
                                        className={`rounded-md px-3 py-2 text-xs font-semibold transition ${isActive ? 'item-selected bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                      >
                                        {option.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                          {!pendingColorIsTransparent && (
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="text-sm font-medium text-gray-700">
                                  Color
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                  <input
                                    type="color"
                                    value={pendingColor}
                                    onChange={(e) => {
                                      setPendingColor(e.target.value);
                                      setPendingColorIsTransparent(false);
                                    }}
                                    className="h-10 w-20 shrink-0 cursor-pointer rounded border border-gray-200 bg-white p-1"
                                  />
                                  <div className="flex shrink-0 flex-wrap gap-2">
                                    {[
                                      '#ffffff',
                                      '#fef3c7',
                                      '#dcfce7',
                                      '#dbeafe',
                                      '#f3e8ff',
                                    ].map((c) => (
                                      <button
                                        key={c}
                                        type="button"
                                        onClick={() => {
                                          setPendingColor(c);
                                          setPendingColorIsTransparent(false);
                                        }}
                                        style={
                                          {
                                            '--swatch-color': c,
                                          } as React.CSSProperties
                                        }
                                        className={`color-swatch h-6 w-6 rounded-full border transition hover:scale-110 ${pendingColor === c ? 'item-selected ring-2 ring-primary-500 ring-offset-1' : ''}`}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Image (Optional)
                      </label>
                      <div className="flex flex-col gap-3">
                        {pendingImage ? (
                          <div className="group relative h-32 w-full overflow-hidden rounded-lg border border-gray-100">
                            <img
                              src={pendingImage}
                              className="h-full w-full object-cover"
                              alt="Preview"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setPendingImage(null);
                                logToggleEvent('goal_image', false);
                              }}
                              className="absolute right-2 top-2 rounded-full bg-black/50 p-1 text-white opacity-0 transition hover:bg-black/70 group-hover:opacity-100"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                width="14"
                                height="14"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path
                                  d="M18 6L6 18M6 6l12 12"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="flex h-32 w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 transition hover:border-primary-300 hover:bg-gray-100"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              width="24"
                              height="24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              className="mb-1 text-gray-400"
                            >
                              <path
                                d="M12 5v14M5 12h14"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span className="text-xs text-gray-500">
                              Upload Image
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setPendingImage(null);
                  }}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => savePending(Boolean(editingId))}
                  disabled={!stripHtml(pendingText).trim() && !pendingImage}
                  className="rounded-lg !bg-indigo-600 px-4 py-2 text-sm font-medium !text-white shadow-sm transition hover:brightness-95 disabled:text-white disabled:opacity-50"
                >
                  {editingId ? 'Update Goal' : 'Add to Board'}
                </button>
              </div>
            </div>
          </Modal>
        )}

        {showSettingsModal && (
          <Modal
            title="Vision Board Settings"
            onClose={() => setShowSettingsModal(false)}
            className="flex max-h-[calc(100vh-2.5rem)] w-full max-w-none flex-col overflow-hidden sm:max-h-[calc(100vh-6rem)] sm:max-w-3xl"
            contentClassName="flex min-h-0 flex-1 flex-col"
            headerClassName="sticky top-0 z-10 -mx-4 mb-4 border-b border-gray-100 bg-white/95 px-4 pb-3 pt-1 sm:-mx-6 sm:px-6 sm:pt-0"
          >
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-5">
                <section className={`${sectionCardClass} space-y-3`}>
                  <div className="text-sm font-semibold text-gray-700">
                    Canvas Background
                  </div>
                  <div className="space-y-3">
                    {backgroundPresets.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-gray-200 bg-white/70 px-4 py-6 text-center text-xs text-gray-500">
                        No preset backgrounds yet.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 sm:grid-cols-3">
                        {backgroundPresets.map((preset) => {
                          const isActive =
                            editorSettings.canvasBackground.type === 'preset' &&
                            editorSettings.canvasBackground.value ===
                              preset.value;
                          return (
                            <button
                              key={preset.id}
                              type="button"
                              onClick={() => {
                                setEditorSettings((prev) => ({
                                  ...prev,
                                  canvasBackground: {
                                    type: 'preset',
                                    value: preset.value,
                                  },
                                  backgroundDisplay: preset.defaultDisplay,
                                }));
                                logOptionSelect(
                                  'background',
                                  preset.label,
                                  preset.value
                                );
                              }}
                              className={`group relative h-20 overflow-hidden rounded-lg border transition ${isActive ? 'item-selected border-primary-500 ring-2 ring-primary-500 ring-offset-2' : 'border-gray-200 hover:border-primary-300'}`}
                              aria-pressed={isActive}
                            >
                              <div
                                className="h-full w-full bg-cover bg-center"
                                style={{
                                  backgroundImage: `url(${preset.value})`,
                                }}
                              />
                              <span className="absolute bottom-1 left-1 rounded bg-white/80 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                                {preset.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                      <button
                        type="button"
                        onClick={() => backgroundInputRef.current?.click()}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-semibold text-gray-700 transition hover:border-primary-300 hover:text-primary-600 sm:w-auto"
                      >
                        Upload Custom Image
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditorSettings((prev) => ({
                            ...prev,
                            canvasBackground: { type: 'none' },
                          }));
                          logOptionSelect('background', 'none', null);
                        }}
                        className={`w-full rounded-lg border px-3 py-2.5 text-sm font-semibold transition sm:w-auto ${
                          editorSettings.canvasBackground.type === 'none'
                            ? 'border-primary-500 text-primary-600'
                            : 'border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-600'
                        }`}
                      >
                        No Background Image
                      </button>
                      {editorSettings.canvasBackground.type === 'custom' && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditorSettings((prev) => ({
                              ...prev,
                              canvasBackground: { type: 'none' },
                            }));
                            logOptionSelect(
                              'background',
                              'custom_removed',
                              null
                            );
                          }}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-primary-300 hover:text-primary-600 sm:w-auto"
                        >
                          Remove Custom Image
                        </button>
                      )}
                    </div>
                    {editorSettings.canvasBackground.type === 'custom' && (
                      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-center">
                        <div
                          className="h-20 w-full overflow-hidden rounded-md border border-gray-200 bg-cover bg-center sm:h-16 sm:w-24"
                          style={{
                            backgroundImage: `url(${editorSettings.canvasBackground.value})`,
                          }}
                        />
                        <div className="text-xs text-gray-500">
                          Custom image selected. Upload a new image to replace
                          it.
                        </div>
                      </div>
                    )}
                    <input
                      ref={backgroundInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleBackgroundFileChange}
                      className="hidden"
                    />
                  </div>
                </section>

                {editorSettings.canvasBackground.type !== 'none' && (
                  <section className={`${sectionCardClass} space-y-3`}>
                    <div className="text-sm font-semibold text-gray-700">
                      Background Display
                    </div>
                    <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 p-1">
                      {(
                        [
                          { id: 'fill', label: 'Fill' },
                          { id: 'repeat', label: 'Repeat' },
                        ] as const
                      ).map((option) => {
                        const isActive =
                          editorSettings.backgroundDisplay === option.id;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => {
                              setEditorSettings((prev) => ({
                                ...prev,
                                backgroundDisplay: option.id,
                              }));
                              logOptionSelect(
                                'layout',
                                'background_display',
                                option.id
                              );
                            }}
                            className={`min-w-[120px] flex-1 rounded-md px-3 py-2.5 text-sm font-semibold transition ${isActive ? 'item-selected bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                )}

                <section className={`${sectionCardClass} space-y-3`}>
                  <div className="text-sm font-semibold text-gray-700">
                    Canvas Corners
                  </div>
                  <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 p-1">
                    {(['square', 'rounded'] as const).map((corner) => {
                      const isActive = editorSettings.canvasCorners === corner;
                      return (
                        <button
                          key={corner}
                          type="button"
                          onClick={() => {
                            setEditorSettings((prev) => ({
                              ...prev,
                              canvasCorners: corner,
                            }));
                            logOptionSelect('layout', 'canvas_corners', corner);
                          }}
                          aria-pressed={isActive}
                          className={`min-w-[120px] flex-1 rounded-md px-3 py-2.5 text-sm font-semibold transition ${isActive ? 'item-selected bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          {corner === 'rounded' ? 'Rounded' : 'Square'}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500">
                    Square corners are the default; rounded adds the softer look
                    for the canvas preview and exports only.
                  </p>
                </section>

                <section className={`${sectionCardClass} space-y-3`}>
                  <div className="text-sm font-semibold text-gray-700">
                    Default Text Size
                  </div>
                  <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 p-1">
                    {FONT_SIZE_OPTIONS.map((option) => {
                      const isActive =
                        editorSettings.defaultFontSize === option.id;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            setEditorSettings((prev) => ({
                              ...prev,
                              defaultFontSize: option.id,
                            }));
                            logOptionSelect(
                              'text_style',
                              'default_font_size',
                              option.id
                            );
                          }}
                          className={`min-w-[96px] flex-1 rounded-md px-3 py-2.5 text-sm font-semibold transition ${isActive ? 'item-selected bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500">
                    Applies to newly added goals only.
                  </p>
                </section>

                <section className={`${sectionCardClass} space-y-3`}>
                  <div className="text-sm font-semibold text-gray-700">
                    Goal Container Shape
                  </div>
                  <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 p-1">
                    {(['square', 'circle', 'heart'] as const).map((shape) => {
                      const isActive =
                        editorSettings.defaultContainerShape === shape;
                      return (
                        <button
                          key={shape}
                          type="button"
                          onClick={() =>
                            setEditorSettings((prev) => ({
                              ...prev,
                              defaultContainerShape: shape,
                            }))
                          }
                          className={`min-w-[100px] flex-1 rounded-md px-3 py-2.5 text-sm font-semibold transition ${isActive ? 'item-selected bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'} flex items-center justify-center gap-2`}
                        >
                          <span
                            className={`h-4 w-4 border border-gray-300 ${shape === 'circle' ? 'rounded-full' : shape === 'heart' ? '' : 'rounded-sm'}`}
                            style={
                              shape === 'heart'
                                ? {
                                    clipPath: 'url(#heart-clip)',
                                    WebkitClipPath: 'url(#heart-clip)',
                                    transform: 'scale(1.2)',
                                  }
                                : {}
                            }
                            aria-hidden="true"
                          />
                          {shape.charAt(0).toUpperCase() + shape.slice(1)}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500">
                    Ignored when a goal background is set to transparent.
                  </p>
                </section>

                <section className={`${sectionCardClass} space-y-3`}>
                  <div className="text-sm font-semibold text-gray-700">
                    Default Goal Shadow
                  </div>
                  <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 p-1">
                    {[
                      { label: 'On', value: true },
                      { label: 'Off', value: false },
                    ].map((option) => {
                      const isActive =
                        editorSettings.defaultHasShadow === option.value;
                      return (
                        <button
                          key={option.label}
                          type="button"
                          onClick={() => {
                            setEditorSettings((prev) => ({
                              ...prev,
                              defaultHasShadow: option.value,
                            }));
                            logOptionSelect(
                              'layout',
                              'default_goal_shadow',
                              option.value
                            );
                          }}
                          className={`min-w-[100px] flex-1 rounded-md px-3 py-2.5 text-sm font-semibold transition ${isActive ? 'item-selected bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500">
                    Applies to newly added goals only.
                  </p>
                </section>

                <section className={`${sectionCardClass} space-y-3`}>
                  <div className="text-sm font-semibold text-gray-700">
                    Goal Card Color
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      type="color"
                      value={editorSettings.defaultCardColor}
                      onChange={(e) =>
                        setEditorSettings((prev) => ({
                          ...prev,
                          defaultCardColor: e.target.value,
                        }))
                      }
                      className="h-11 w-24 cursor-pointer rounded border border-gray-200 bg-white p-1 sm:h-10 sm:w-20"
                    />
                    <div className="flex flex-wrap gap-2">
                      {[
                        '#ffffff',
                        '#fef3c7',
                        '#dcfce7',
                        '#dbeafe',
                        '#f3e8ff',
                      ].map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() =>
                            setEditorSettings((prev) => ({
                              ...prev,
                              defaultCardColor: c,
                            }))
                          }
                          style={
                            {
                              '--swatch-color': c,
                            } as React.CSSProperties
                          }
                          className={`color-swatch h-9 w-9 rounded-full border transition hover:scale-110 sm:h-6 sm:w-6 ${editorSettings.defaultCardColor === c ? 'item-selected ring-2 ring-primary-500 ring-offset-1' : ''}`}
                          aria-label={`Set card color to ${c}`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Applies only to new goals with visible backgrounds.
                  </p>
                </section>

                {goals.length > 0 && (
                  <section className={`${sectionCardClass} space-y-3`}>
                    <div className="text-sm font-semibold text-gray-700">
                      Board actions
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowResetConfirmModal(true)}
                      className={`${actionButtonBase} w-full sm:w-auto`}
                    >
                      Reset Vision Board
                    </button>
                    <p className="text-xs text-gray-500">
                      Clears all items from your current board. This cannot be
                      undone.
                    </p>
                  </section>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
              {showSettingsSaved && (
                <span className="text-xs font-medium text-emerald-600 sm:mr-auto">
                  Changes saved.
                </span>
              )}
              <button
                type="button"
                onClick={() => setShowSettingsModal(false)}
                className={`${actionButtonBase} w-full sm:w-auto`}
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => setEditorSettings(DEFAULT_EDITOR_SETTINGS)}
                className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:border-primary-300 hover:text-primary-600 sm:w-auto"
              >
                Reset to Defaults
              </button>
            </div>
          </Modal>
        )}

        {confirmDeleteId && (
          <Modal title="Delete Goal" onClose={() => setConfirmDeleteId(null)}>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete this goal? This cannot be
                undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(null)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    deleteGoal(confirmDeleteId);
                    setConfirmDeleteId(null);
                  }}
                  className="rounded-lg !bg-indigo-600 px-4 py-2 text-sm font-medium !text-white shadow-sm transition hover:brightness-95 disabled:text-white disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </Modal>
        )}

        {showResetConfirmModal && (
          <Modal
            title="Reset Vision Board"
            onClose={() => setShowResetConfirmModal(false)}
          >
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">
                This will remove all items from your vision board. This action
                can’t be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  autoFocus
                  onClick={() => setShowResetConfirmModal(false)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setGoals([]);
                    setShowResetConfirmModal(false);
                    setShowSettingsModal(false);
                    logVisionBoardInteraction({
                      eventType: 'board_reset',
                      eventCategory: 'board',
                    });
                  }}
                  className={`${actionButtonBase}`}
                >
                  Reset Board
                </button>
              </div>
            </div>
          </Modal>
        )}

        {showSaveModal && (
          <Modal
            title="Name your Vision Board"
            className="w-full sm:max-w-2xl"
            onClose={() => {
              if (isSaving) return;
              setShowSaveModal(false);
              setBoardTitle('');
              setBoardTitleError(null);
            }}
          >
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                confirmSaveBoard();
              }}
            >
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Vision Board Name
                </label>
                <input
                  type="text"
                  value={boardTitle}
                  onChange={(event) => {
                    setBoardTitle(event.target.value);
                    setBoardTitleError(null);
                  }}
                  placeholder="My 2026 Vision"
                  maxLength={MAX_BOARD_TITLE_LENGTH}
                  required
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                {boardTitleError && (
                  <div className="mt-1 text-xs text-red-600">
                    {boardTitleError}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowSaveModal(false);
                    setBoardTitle('');
                    setBoardTitleError(null);
                  }}
                  disabled={isSaving}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-lg !bg-indigo-600 px-4 py-2 text-sm font-medium !text-white shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </Modal>
        )}

        {showVisionBoardLockedModal &&
          (() => {
            const single =
              visionBoardBadgeRequirements.length === 1
                ? visionBoardBadgeRequirements[0]
                : null;

            return (
              <Modal
                title="Unlock Vision Board Backgrounds"
                onClose={() => setShowVisionBoardLockedModal(false)}
                className="max-w-xl"
                {...(single
                  ? {
                      style: {
                        backgroundColor: single.color || undefined,
                      },
                      textColorClass: single.color ? 'text-white' : undefined,
                      closeButtonClass: single.color
                        ? 'text-white transition hover:bg-white/10 hover:text-white'
                        : undefined,
                    }
                  : undefined)}
              >
                {single ? (
                  <div className="flex justify-center">
                    <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg">
                      <div className="flex items-center gap-6 sm:gap-10">
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewBadge(single)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setPreviewBadge(single);
                            }
                          }}
                          className="h-56 w-56 flex-shrink-0 cursor-pointer overflow-hidden rounded-full border border-gray-100 shadow-inner"
                          style={{
                            backgroundColor: single.color ?? undefined,
                          }}
                        >
                          {single.image_url ? (
                            <img
                              src={
                                buildBadgeAssetUrl(single.image_url) ||
                                undefined
                              }
                              alt={single.name || 'badge'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-3xl">
                              {single.name?.slice(0, 1) ?? '🏅'}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 text-left">
                          {single.name && (
                            <div className="text-2xl font-semibold text-gray-900">
                              {single.name}
                            </div>
                          )}
                          <div className="mt-4 text-sm text-gray-700">
                            <p>{visionBoardUnlockMessage}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6">
                        <Button
                          type="button"
                          className="item-selected w-full"
                          onClick={() => navigate('/badges')}
                        >
                          View All Badges
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : visionBoardBadgeRequirements.length > 0 ? (
                  <div className="flex justify-center gap-3">
                    {visionBoardBadgeRequirements.map((badge) => (
                      <div
                        key={badge.id || badge.name}
                        className="flex flex-col items-center"
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewBadge(badge)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setPreviewBadge(badge);
                            }
                          }}
                          className="h-48 w-48 cursor-pointer overflow-hidden rounded-full focus:outline-none focus:ring-2"
                          style={{
                            backgroundColor: badge.color ?? undefined,
                          }}
                        >
                          {badge.image_url ? (
                            <img
                              src={
                                buildBadgeAssetUrl(badge.image_url) || undefined
                              }
                              alt={badge.name || 'badge'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-xl">
                              {badge.name?.slice(0, 1) ?? '🏅'}
                            </div>
                          )}
                        </div>
                        {badge.name && (
                          <div
                            className="mt-2 text-xs font-semibold"
                            style={{
                              color: badge.color ? '#fff' : undefined,
                            }}
                          >
                            {badge.name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}
                {single ? null : (
                  <>
                    <p className="text-sm text-gray-600">
                      {visionBoardUnlockMessage}
                    </p>
                    <Button
                      type="button"
                      className="item-selected mt-6 w-full"
                      onClick={() => navigate('/badges')}
                    >
                      View All Badges
                    </Button>
                  </>
                )}
              </Modal>
            );
          })()}

        {previewBadge && (
          <Modal
            title={previewBadge.name ?? undefined}
            onClose={() => setPreviewBadge(null)}
            className="max-w-xl"
            style={{ backgroundColor: previewBadge.color || undefined }}
            textColorClass="text-white"
            closeButtonClass="text-white transition hover:bg-white/10 hover:text-white"
          >
            <div className="flex justify-center">
              <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg">
                <div className="flex items-center gap-6 text-gray-900">
                  <div
                    className="h-56 w-56 flex-shrink-0 overflow-hidden rounded-full border border-gray-100 shadow-inner"
                    style={{
                      backgroundColor: previewBadge.color ?? undefined,
                    }}
                  >
                    {previewBadge.image_url ? (
                      <img
                        src={
                          buildBadgeAssetUrl(previewBadge.image_url) ||
                          undefined
                        }
                        alt={previewBadge.name ?? 'badge'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-3xl">
                        {previewBadge.name?.slice(0, 1) ?? '🏅'}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 text-left">
                    {previewBadge.name && (
                      <div className="text-2xl font-semibold text-gray-900">
                        {previewBadge.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </Container>
  );
}
